// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/**
 * lists.css — Sistema canónico de listados del CMDB.
 *
 * POR QUÉ EXISTE ESTE FICHERO
 * ---------------------------------------------------------------------------
 * Los listados (servidores, ubicaciones, racks, PDU/switch/parches, tomas de
 * pared, impresoras, estaciones...) habían crecido por copia: cada página se
 * llevó el CSS de la anterior y le cambió el prefijo. El resultado medido:
 *
 *   · \`ServersList.css\` y \`LocationsList.css\` eran idénticos en 337 de sus 357
 *     líneas (solo cambiaba \`servers-\` por \`locations-\`), con derivas pequeñas
 *     que son justo la falta de uniformidad: 150px contra 160px, \`--accent\`
 *     contra \`#3b82f6\`, etc.
 *   · \`WallOutletsList.css\` (779 líneas) era de facto el CSS compartido de
 *     todos los listados, pero definía las mismas clases DOS veces: primero
 *     con variables y luego con colores claros en duro (\`#1e293b\`, \`#64748b\`,
 *     \`#cbd5e1\`) que eran los que acababan mandando. Además tenía un bloque
 *     roto: las reglas del kanban de servicios estaban pegadas DENTRO de
 *     \`.wol-input { ... }\`, así que el navegador descartaba reglas al parsear.
 *
 * Este fichero sustituye todo eso por **una sola definición por cosa**, hecha
 * con las variables del sistema de diseño, de modo que los skins de
 * \`styles/skins.css\` la siguen sin tocar nada.
 *
 * Los tres CSS viejos (\`ServersList.css\`, \`LocationsList.css\`, \`RacksList.css\`)
 * y \`WallOutletsList.css\` YA NO EXISTEN: al migrar los 26 componentes que aún
 * lo importaban se les quitó el import, así que no queda CSS de listado fuera
 * de este fichero.
 *
 * CÓMO SE APLICA SIN REESCRIBIR LAS PÁGINAS
 * ---------------------------------------------------------------------------
 * Se importa el ÚLTIMO de las hojas "normales" (después de \`overrides.css\` y
 * antes de \`skins.css\`), así que gana a los CSS de cada página. Y cada regla
 * incluye como alias las clases históricas (\`wol-*\`, \`pel-*\` y las
 * \`*-table\` de cada listado) para que las páginas actuales adopten el diseño
 * sin cambiar su JSX.
 *
 * Al añadir un listado nuevo: usa las clases \`list-*\` y no añadas CSS propio.
 * El orden canónico de la página es
 *   \`.list-page > .list-head + .list-kpis + .list-toolbar + .list-table-wrap\`
 * y, si hay diálogos, \`.list-overlay > .list-dialog\`.
 */

/* ═══════════════════════════════════════════════════════════════════════════
   1. Página y cabecera
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-page, .wol-container, .pel-container) {
  max-width: 1400px;
  /* El padding lo pone \`.main-content\`; aquí solo el aire inferior. */
  padding: 0 0 2rem;
  animation: list-fade-in 0.3s ease;
}

:is(.list-head, .wol-header, .pel-header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

:is(.list-head-left, .wol-header-left, .pel-header-left) {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
}

/* Grupo de la derecha de la cabecera: conmutador de vistas + botón de alta.
   Antes cada página resolvía esto con estilos en línea. */
:is(.list-head-actions, .wol-header-actions) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

:is(.list-head-icon, .wol-icon, .pel-header-icon) {
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  color: var(--accent);
  opacity: 0.95;
}

:is(.list-title, .wol-title, .pel-title) {
  margin: 0;
  font-size: 1.5rem;
  font-weight: var(--fw-bold);
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: var(--text-primary);
}

:is(.list-subtitle, .wol-subtitle, .pel-subtitle) {
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* ── Listado EMBEBIDO en una sección con pestañas (Agente CMDB) ─────────────
   La página ya tiene su \`<h1>\` y su barra de pestañas; el listado de dentro es
   una sección, no una pantalla. Para que no compitan dos títulos del mismo
   tamaño, el embebido baja a \`<h2>\` (\`.list-section-title\`), el icono se
   reduce y la cabecera se separa con una línea en vez de con aire. */
.list-page--embedded > .list-head {
  align-items: flex-end;
  margin-bottom: 1rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid var(--border);
}

:is(.list-section-title) {
  margin: 0;
  font-size: 1.05rem;
  font-weight: var(--fw-semibold);
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--text-primary);
}

.list-section-subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: var(--text-secondary);
}

.list-page--embedded .list-head-icon {
  width: 1.5rem;
  height: 1.5rem;
  opacity: 0.85;
}

/* Botón de volver (vistas de detalle dentro de un listado) */
:is(.list-back-btn, .wol-back-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.5rem;
  margin-right: 0.25rem;
  border: 0;
  border-radius: var(--radius-md);
  background: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

:is(.list-back-btn, .wol-back-btn):hover {
  background: var(--bg-toolbar);
  color: var(--text-primary);
}

/* ═══════════════════════════════════════════════════════════════════════════
   2. Barra de filtros y controles
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-toolbar, .wol-filters, .pel-filters) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.6rem 0.7rem;
  margin-bottom: 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-toolbar);
}

:is(.list-toolbar-left, .wol-list-controls, .pel-toolbar-left) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Los filtros van juntos para poder plegarlos de golpe en pantallas bajas
   (\`ListPage\` los esconde detrás del botón «Filtros»). */
.list-toolbar-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
}

.list-toolbar-filters.is-collapsed {
  display: none;
}

/* Botón que abre/cierra la barra de filtros (solo en ventanas bajas/estrechas) */
.list-filters-btn {
  white-space: nowrap;
}

.list-filters-btn.is-active {
  border-color: rgba(var(--accent-rgb), 0.5);
  color: var(--accent-l);
}

.list-filters-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--accent);
  color: #fff;
  font-size: 0.7rem;
  font-weight: var(--fw-bold);
  line-height: 1;
}

:is(.list-toolbar-right, .wol-inline-list-controls) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
}

:is(.list-control-label, .wol-list-control-label) {
  font-size: 0.68rem;
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

/* ── Conmutador de vistas (lista / fichas / árbol) ──────────────────────────
   Solo pinta las vistas que el listado implementa (ver \`common/ViewSwitcher\`).
   Lleva el nombre de cada vista a propósito: el conmutador anterior era solo
   iconos y no se sabía qué hacía cada botón. */
.list-views {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid var(--border);
  border-radius: var(--skin-control-radius, var(--radius-md));
  background: var(--bg-input);
}

.list-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.3rem 0.6rem;
  border: 0;
  border-radius: calc(var(--skin-control-radius, var(--radius-md)) - 2px);
  background: transparent;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: var(--fw-semibold);
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.list-view-btn svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  stroke-width: 1.8;
}

.list-view-btn:hover:not(.is-active) {
  background: rgba(var(--accent-rgb), 0.12);
  color: var(--text-primary);
}

.list-view-btn.is-active {
  background: var(--accent);
  color: #fff;
}

.list-view-btn--reset {
  padding: 0.3rem 0.45rem;
  margin-left: 2px;
  border-left: 1px solid var(--border);
  border-radius: 0 calc(var(--skin-control-radius, var(--radius-md)) - 2px)
    calc(var(--skin-control-radius, var(--radius-md)) - 2px) 0;
  color: var(--text-muted);
}

/* En pantallas estrechas queda el icono: el nombre ya lo dice el \`title\`. */
@media (max-width: 900px) {
  .list-view-btn-label {
    display: none;
  }
}

/* Contenedor de una vista propia del listado (fichas, árbol…) */
.list-view-body {
  animation: list-fade-in 0.2s ease;
  /* Permite que la vista encoja dentro de un contenedor flex/grid: si no, un
     lienzo ancho (vista Frontal de racks) estira la página en vez de scrollear. */
  min-width: 0;
}

/* ── Plegado del árbol (vista «árbol» de ubicaciones) ─────────────────────── */
.list-tree-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.list-tree-toggle svg {
  width: 13px;
  height: 13px;
  stroke-width: 2.2;
}

.list-tree-toggle:hover {
  background: rgba(var(--accent-rgb), 0.14);
  color: var(--accent-l);
}

/* Hueco de las hojas del árbol: alinea el icono con el de los nodos plegables */
.list-tree-spacer {
  display: inline-block;
  width: 18px;
  flex-shrink: 0;
}

/* Rejilla de fichas: la usan las vistas "card" de los listados */
.list-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.9rem;
}

.list-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.9rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.18s ease, transform 0.12s ease;
}

.list-card:hover {
  border-color: rgba(var(--accent-rgb), 0.45);
  box-shadow: var(--shadow-lg);
  transform: translateY(-1px);
}

.list-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.list-card-title {
  font-size: 0.95rem;
  font-weight: var(--fw-bold);
  color: var(--text-primary);
  line-height: 1.25;
}

.list-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.list-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: auto;
}

/* ── Barra de ocupación de nodos (chasis multinodo) ──────────────────────── */
.chassis-node-bar {
  display: inline-flex;
  align-items: center;
  min-width: 96px;
}

.chassis-node-bar-track {
  flex: 1 1;
  height: 6px;
  border-radius: 3px;
  background: var(--slate-200);
  overflow: hidden;
}

.chassis-node-bar-fill {
  display: block;
  height: 100%;
  transition: width 0.3s ease;
}

/* ── Sub-filas dentro de una vista (nodos de un chasis) ──────────────────── */
.list-subrows {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 0.35rem;
}

.list-subrow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.35rem 0.5rem;
  border: 0;
  border-radius: var(--radius-sm);
  background: var(--bg-toolbar);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.82rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.list-subrow:hover {
  background: rgba(var(--accent-rgb), 0.12);
}

.list-subrow-name {
  font-weight: var(--fw-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Kanban de listados (servidores por entorno) ─────────────────────────── */
.list-kanban {
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}

.list-kanban-col {
  flex: 1 0 260px;
  max-width: 340px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-toolbar);
}

.list-kanban-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.82rem;
  font-weight: var(--fw-bold);
  color: var(--text-primary);
}

.list-kanban-body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.65rem;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
}

/* Fila resaltada por \`?destacar=<id>\` (convención de todos los módulos) */
.list-row-highlight td {
  background-color: rgba(245, 158, 11, 0.14) !important;
}

.list-row-highlight:focus-visible td {
  outline: 2px solid var(--amber-600);
  outline-offset: -2px;
}

/* Caducidad: naranja si queda poco, rojo si ya venció (\`rowClassName\`).
   Se pinta la fila entera y una barra en la primera celda, sin tocar el texto:
   así se lee igual en tema claro y oscuro. */
.list-table tr.list-row-warn > td {
  background-color: rgba(245, 158, 11, 0.10);
}

.list-table tr.list-row-warn > td:first-child {
  box-shadow: inset 3px 0 0 var(--amber-600, #d97706);
}

.list-table tr.list-row-danger > td {
  background-color: rgba(239, 68, 68, 0.10);
}

.list-table tr.list-row-danger > td:first-child {
  box-shadow: inset 3px 0 0 var(--red-600, #dc2626);
}

/* Cabecera de grupo de la tabla (ListPage \`groupBy\`): fila a lo ancho, con el
   triángulo de plegar y el recuento. Cada grupo se pliega por separado. */
.list-group-row td {
  background: var(--bg-toolbar);
  border-top: 1px solid var(--border);
  padding: 0.35rem 0.75rem;
}

.list-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: none;
  padding: 0;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

.list-group-toggle strong {
  color: var(--text-muted);
  font-weight: 600;
}

/* ── Selección múltiple y edición masiva ─────────────────────────────────
   La lista pasa \`selectable\` y \`bulkActions\`: aparece una casilla por fila
   (y otra en la cabecera para «todo lo visible») y, mientras haya algo
   seleccionado, una barra con las acciones. */
.list-th-select,
.list-select-cell {
  width: 38px;
  text-align: center;
  padding-left: 10px !important;
  padding-right: 0 !important;
}

.list-th-select input,
.list-select-cell input {
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: var(--accent);
  cursor: pointer;
  vertical-align: middle;
}

.list-select-cell {
  cursor: default;
}

.list-bulk-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(var(--accent-rgb), 0.45);
  border-radius: var(--skin-control-radius, var(--radius-md));
  background: rgba(var(--accent-rgb), 0.10);
}

.list-bulk-count {
  font-size: 0.85rem;
  color: var(--text-primary);
}

.list-bulk-count strong {
  font-size: 0.95rem;
}

.list-bulk-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.list-bulk-bar > .list-btn-secondary {
  margin-left: auto;
}

/* Campos del diálogo de colocación masiva */
.bulk-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 12px 0;
}

.bulk-field > span {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.bulk-field .list-select {
  width: 100%;
}

:is(.list-control-select, .wol-list-control-select) {
  min-width: 220px;
}

/* Campos: búsqueda, selectores y entradas de los listados */
:is(.list-search, .list-select, .list-input,
    .wol-search, .wol-select, .wol-input,
    .pel-search, .pel-select, .pel-filter-select, .pel-input) {
  min-width: 200px;
  padding: 0.5rem 0.85rem;
  border: 1px solid var(--border);
  border-radius: var(--skin-control-radius, var(--radius-md));
  background: var(--bg-input);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.875rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

:is(.list-search, .wol-search, .pel-search) {
  flex: 1 1;
}

:is(.list-select, .wol-select, .pel-select, .pel-filter-select) {
  cursor: pointer;
  padding-right: 2rem;
  appearance: none;
  -webkit-appearance: none;
  /* Flecha dibujada con el color del texto secundario, así vale en claro y oscuro */
  background-image: linear-gradient(45deg, transparent 50%, currentColor 50%),
    linear-gradient(135deg, currentColor 50%, transparent 50%);
  background-position: right 1.1rem center, right 0.75rem center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}

:is(.list-search, .list-select, .list-input, .wol-search, .wol-select, .wol-input,
    .pel-search, .pel-select, .pel-filter-select, .pel-input)::placeholder {
  color: var(--text-muted);
}

:is(.list-search, .list-select, .list-input, .wol-search, .wol-select, .wol-input,
    .pel-search, .pel-select, .pel-filter-select, .pel-input):focus {
  outline: none;
  border-color: rgba(var(--accent-rgb), 0.55);
  box-shadow: var(--skin-focus-ring, 0 0 0 3px rgba(var(--accent-rgb), 0.18));
}

:is(.list-select, .wol-select, .pel-select, .pel-filter-select) option {
  background: var(--bg-surface);
  color: var(--text-primary);
}

/* KPIs de cabecera de un listado (los usan tomas de pared y otros) */
:is(.list-kpis, .wol-kpi-grid) {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.65rem;
  margin: 0.75rem 0 1rem;
}

:is(.list-kpi, .wol-kpi-card) {
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-card);
}

:is(.list-kpi-value, .wol-kpi-value) {
  font-size: 1.2rem;
  font-weight: var(--fw-bold);
  line-height: 1.1;
  color: var(--text-primary);
}

:is(.list-kpi-label, .wol-kpi-label) {
  margin-top: 0.2rem;
  font-size: 0.68rem;
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

/* KPIs CLICABLES (Estaciones de Trabajo): cada chip aplica su filtro y
   «Total» los quita todos. El contenedor deja de ser rejilla. */
.list-kpis--chips {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.6rem;
}

.list-kpi-chip {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  cursor: pointer;
  text-align: left;
}

.list-kpi-chip:hover {
  border-color: var(--accent);
}

.list-kpi-chip--on {
  border-color: var(--accent);
  background: var(--bg-toolbar);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.list-kpi-group {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
}

/* ═══════════════════════════════════════════════════════════════════════════
   3. Tabla
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap,
    .servers-table-wrapper, .locations-table-wrapper, .racks-table-wrapper) {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  max-height: calc(100vh - 300px);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  box-shadow: var(--shadow-sm);
}

/* \`table-layout\` no se fija a propósito: las columnas deben repartirse solas. */
:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  color: var(--text-primary);
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead th {
  position: sticky;
  top: 0;
  z-index: 8;
  padding: 12px 12px;
  border-bottom: 1px solid var(--border);
  /* OPACO a propósito: la cabecera es sticky y con un fondo translúcido se ven
     las filas por debajo al hacer scroll. \`--bg-surface\` es la variable opaca
     del tema; los skins la sobreescriben con \`--skin-table-head-bg\`. */
  background: var(--bg-surface, var(--bg-card));
  color: var(--text-muted);
  font-size: 10.5px;
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  text-align: left;
  /* Los encabezados SÍ pueden partir en dos líneas. Con \`nowrap\` la suma de
     anchos de los 11 encabezados de servidores superaba el contenedor y la
     última columna quedaba cortada tras un scroll horizontal. */
  white-space: normal;
  line-height: 1.3;
  vertical-align: bottom;
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td {
  padding: 13px 12px;
  border-bottom: 1px solid var(--border);
  border-top: 0;
  vertical-align: middle;
  color: var(--text-primary);
  line-height: 1.4;
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:last-child td {
  border-bottom: 0;
}

/* Cebra muy suave, y en los skins la marca el propio skin */
:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:nth-child(even) td {
  background-color: transparent;
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:nth-child(odd) td {
  background-color: var(--skin-row-stripe, var(--bg-toolbar));
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:hover td {
  background-color: var(--skin-row-hover, rgba(var(--accent-rgb), 0.08));
}

/* Compensación de los estilos en línea que algunas páginas usan para las filas */
:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:is(.servers-row-even, .locations-row-even, .racks-row-even, .wol-row-even) td {
  background-color: transparent !important;
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:is(.servers-row-odd, .locations-row-odd, .racks-row-odd, .wol-row-odd) td {
  background-color: var(--skin-row-stripe, var(--bg-toolbar)) !important;
}

:is(.list-th-sortable, .wol-th-sortable, .list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead th:is(.list-th-sortable, .wol-th-sortable, .sortable, .servers-th, .locations-th, .racks-th) {
  cursor: pointer;
  -webkit-user-select: none;
          user-select: none;
}

:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead th:is(.list-th-sortable, .wol-th-sortable, .sortable, .servers-th):hover {
  background: rgba(var(--accent-rgb), 0.1);
  color: var(--text-secondary);
}

:is(.list-sort-indicator, .wol-sort-indicator, .sort-indicator) {
  margin-left: 0.35rem;
  font-size: 0.8rem;
  font-weight: var(--fw-bold);
  color: var(--accent);
}

:is(.list-sort-indicator, .wol-sort-indicator, .sort-indicator) sup {
  margin-left: 1px;
  font-size: 0.62rem;
}

:is(.list-row-clickable, .wol-row-clickable) {
  cursor: pointer;
}

:is(.list-row-clickable, .wol-row-clickable):focus-visible td {
  outline: 2px solid rgba(var(--accent-rgb), 0.8);
  outline-offset: -2px;
}

/* Fila de grupo plegable */
:is(.list-group-row, .wol-group-row) td {
  padding: 0.45rem 0.9rem;
  background: var(--bg-toolbar) !important;
  border-bottom: 1px solid var(--border);
}

:is(.list-group-toggle, .wol-group-toggle) {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

:is(.list-group-toggle, .wol-group-toggle) span {
  margin: 0 0.3rem;
}

:is(.list-group-toggle, .wol-group-toggle) strong {
  color: var(--text-secondary);
  font-weight: var(--fw-semibold);
}

/* ═══════════════════════════════════════════════════════════════════════════
   4. Contenido en línea de la tabla: códigos, tipos y estados
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-code, .wol-code, .pel-code) {
  padding: 2px 7px;
  border-radius: var(--radius-sm);
  background: rgba(var(--accent-rgb), 0.14);
  color: var(--accent-l);
  font-family: var(--font-mono);
  font-size: 0.8125rem;
}

:is(.list-type, .wol-type, .pel-model, .pel-name) {
  font-family: var(--font-mono);
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* El color funcional sale del token del tema: vale en claro y en oscuro */
:is(.list-state-on, .wol-connected) { color: var(--green-600); font-size: 0.8125rem; }
:is(.list-state-off, .wol-free, .pel-unplaced) { color: var(--text-muted); font-size: 0.8125rem; }
:is(.list-state-cable, .wol-cable) { color: var(--text-muted); font-size: 0.75rem; margin-left: 4px; }

:is(.list-badge, .wol-badge, .pel-badge) {
  display: inline-block;
  padding: 3px 9px;
  border-radius: var(--skin-chip-radius, 12px);
  font-size: 0.72rem;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.02em;
}

:is(.list-badge--on, .wol-badge--active, .pel-badge--active) {
  background: rgba(22, 163, 74, 0.14);
  color: var(--green-600);
}

:is(.list-badge--off, .wol-badge--inactive, .pel-badge--offline) {
  background: rgba(220, 38, 38, 0.14);
  color: var(--red-600);
}

:is(.list-badge--warn, .pel-badge--maintenance) {
  background: rgba(217, 119, 6, 0.16);
  color: var(--amber-600);
}

/* Dos tonos más, para no tener que inventar colores en línea en cada pantalla:
   «accent» es un estado en curso (no es bueno ni malo todavía) y «muted» es un
   estado apagado (cancelado) que no debe leerse como error. */
:is(.list-badge--accent) {
  background: rgba(var(--accent-rgb), 0.16);
  color: var(--accent-l);
}

:is(.list-badge--muted) {
  background: var(--bg-toolbar);
  color: var(--text-secondary);
  border: 1px solid var(--border);
}

:is(.list-hint, .pel-hint) {
  font-size: 0.8rem;
  color: var(--text-muted);
}

/* ═══════════════════════════════════════════════════════════════════════════
   5. Columna de acciones
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-actions, .wol-actions, .pel-actions, .servers-actions, .locations-actions, .racks-actions) {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

:is(.list-actions-header, .servers-actions-header, .locations-actions-header, .racks-actions-header) {
  text-align: right;
  width: 1%;
  white-space: nowrap;
}

:is(.list-action-btn, .list-icon-btn,
    .wol-action-btn, .wol-btn-sm, .wol-btn-sm-edit, .wol-btn-sm-delete,
    .pel-action-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--skin-control-radius, 6px);
  background: var(--bg-input);
  color: var(--text-secondary);
  font-size: 0;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

:is(.list-action-btn, .list-icon-btn, .wol-action-btn, .wol-btn-sm,
    .wol-btn-sm-edit, .wol-btn-sm-delete, .pel-action-btn) svg {
  width: 15px;
  height: 15px;
  stroke-width: 1.8;
}

:is(.list-action-btn, .list-icon-btn, .wol-action-btn, .wol-btn-sm,
    .wol-btn-sm-edit, .pel-action-btn):hover {
  background: rgba(var(--accent-rgb), 0.14);
  border-color: rgba(var(--accent-rgb), 0.5);
  color: var(--accent-l);
}

:is(.list-action-btn--danger, .wol-action-btn--delete, .wol-btn-sm-delete,
    .pel-action-btn--delete):hover {
  background: rgba(220, 38, 38, 0.14);
  border-color: rgba(220, 38, 38, 0.45);
  color: var(--red-600);
}

.action-icon {
  font-size: 0.95rem;
}

/* ═══════════════════════════════════════════════════════════════════════════
   6. Botones
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-btn-primary, .wol-btn-primary, .pel-btn-primary) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1rem;
  border: 0;
  border-radius: var(--skin-control-radius, var(--radius-md));
  background: var(--skin-btn-primary-bg, var(--accent));
  color: #fff;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: var(--fw-semibold);
  white-space: nowrap;
  cursor: pointer;
  transition: filter 0.15s ease, box-shadow 0.18s ease, transform 0.12s ease;
}

:is(.list-btn-primary, .wol-btn-primary, .pel-btn-primary) svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

:is(.list-btn-primary, .wol-btn-primary, .pel-btn-primary):hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 0 8px 20px -10px rgba(var(--accent-rgb), 0.8);
  transform: translateY(-1px);
}

:is(.list-btn-secondary, .wol-btn-secondary, .pel-btn-secondary) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--skin-control-radius, var(--radius-md));
  background: var(--bg-toolbar);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

:is(.list-btn-secondary, .wol-btn-secondary, .pel-btn-secondary) svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

:is(.list-btn-secondary, .wol-btn-secondary, .pel-btn-secondary):hover:not(:disabled) {
  border-color: rgba(var(--accent-rgb), 0.45);
  color: var(--text-primary);
}

:is(.list-btn-danger, .wol-btn-danger, .pel-btn-danger) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(220, 38, 38, 0.35);
  border-radius: var(--skin-control-radius, var(--radius-md));
  background: rgba(220, 38, 38, 0.12);
  color: var(--red-600);
  font-family: inherit;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

:is(.list-btn-danger, .wol-btn-danger, .pel-btn-danger):hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.2);
  border-color: rgba(220, 38, 38, 0.55);
}

:is(.list-btn-primary, .list-btn-secondary, .list-btn-danger,
    .wol-btn-primary, .wol-btn-secondary, .wol-btn-danger,
    .pel-btn-primary, .pel-btn-secondary, .pel-btn-danger):disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Botón pequeño de acción con texto (presets de puertos, etc.) */
:is(.list-chip-btn, .pel-preset-btn) {
  padding: 0.3rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: var(--skin-chip-radius, var(--radius-md));
  background: var(--bg-toolbar);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: var(--fw-semibold);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

:is(.list-chip-btn, .pel-preset-btn):hover,
:is(.list-chip-btn, .pel-preset-btn).active {
  background: rgba(var(--accent-rgb), 0.14);
  border-color: rgba(var(--accent-rgb), 0.5);
  color: var(--accent-l);
}

/* ═══════════════════════════════════════════════════════════════════════════
   7. Menú de columnas
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-column-toggle, .wol-column-toggle, .column-toggle-menu) {
  position: relative;
}

:is(.list-column-dropdown, .wol-column-dropdown, .column-menu-dropdown) {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 50;
  margin-top: 0.5rem;
  padding: 0.5rem;
  min-width: 240px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  /* Superficie OPACA: en los skins Lumina \`--bg-card\` es translúcido y un
     desplegable flotando sobre la tabla se volvería ilegible. */
  background: var(--bg-surface, var(--bg-card));
  box-shadow: var(--shadow-lg);
  animation: list-slide-down 0.18s ease;
}

:is(.list-column-item, .wol-column-item, .column-menu-item) {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: 0.86rem;
  cursor: pointer;
  -webkit-user-select: none;
          user-select: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}

:is(.list-column-item, .wol-column-item, .column-menu-item):hover {
  background-color: rgba(var(--accent-rgb), 0.1);
  color: var(--text-primary);
}

:is(.list-column-item, .wol-column-item, .column-menu-item) input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin-right: 0.75rem;
  accent-color: var(--accent);
  cursor: pointer;
}

/* ═══════════════════════════════════════════════════════════════════════════
   8. Diálogos: confirmación y formulario
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-overlay, .wol-overlay, .pel-modal-overlay) {
  position: fixed;
  inset: 0;
  /* Por encima del menú lateral (1100) y de los modales de Bootstrap (1055):
     con 1050 el diálogo salía DETRÁS del menú. El \`Dialog\` se pinta además en
     \`document.body\` (portal), porque dentro de un ancestro con \`transform\` el
     \`position: fixed\` se queda pegado a ese ancestro. */
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--modal-overlay);
  backdrop-filter: blur(4px);
}

:is(.list-dialog, .wol-confirm, .pel-confirm-modal) {
  width: 100%;
  max-width: 420px;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  /* Igual que el menú de columnas: superficie opaca para que el modal no se
     transparente en los skins Lumina. */
  background: var(--bg-surface, var(--bg-card));
  box-shadow: var(--shadow-lg);
}

:is(.list-dialog, .wol-confirm, .pel-confirm-modal) h4 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: var(--fw-bold);
  color: var(--text-primary);
}

/* ── Diálogo común: cabecera fija, cuerpo con scroll y acciones a la vista ──
   Sin esto, un diálogo con contenido largo (plantillas, listas de equipos)
   empujaba los botones fuera de la pantalla. El icono de la cabecera se
   acota: un SVG sin tamaño se pinta a todo el ancho disponible. */
.list-dialog {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
}

.list-dialog > h4 {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.list-dialog-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  color: var(--accent);
}

.list-dialog-icon svg {
  width: 20px;
  height: 20px;
}

.list-dialog-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Un pelo de aire para que el foco de los campos no se recorte. */
  padding-right: 2px;
}

.list-dialog > .list-dialog-actions {
  flex: none;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
}

:is(.list-dialog, .wol-confirm, .pel-confirm-modal) p {
  margin: 0 0 1.25rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-secondary);
}

:is(.list-dialog-actions, .wol-confirm-actions, .pel-confirm-actions) {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

:is(.list-form-modal, .wol-form-modal, .pel-form-modal) {
  width: 100%;
  max-width: 580px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--bg-surface);
  box-shadow: var(--shadow-lg);
}

:is(.list-form-header, .wol-form-header, .pel-form-header) {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: transparent;
}

:is(.list-form-header, .wol-form-header, .pel-form-header) :is(h3, h4) {
  flex: 1 1;
  margin: 0;
  font-size: 1rem;
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
}

:is(.list-form-body, .wol-form-body, .pel-form-body) {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
}

:is(.list-form-row, .wol-form-row, .pel-form-row) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

:is(.list-form-field, .wol-form-field, .pel-form-field) {
  display: flex;
  flex: 1 1;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 120px;
}

:is(.list-form-field, .wol-form-field, .pel-form-field) :is(label, span:first-child) {
  font-size: 0.68rem;
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-muted);
}

:is(.list-form-actions, .wol-form-actions, .pel-form-actions) {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
}

:is(.list-close, .wol-close, .pel-form-close) {
  padding: 4px 8px;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-secondary);
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

:is(.list-close, .wol-close, .pel-form-close):hover {
  background: var(--bg-toolbar);
  color: var(--text-primary);
}

/* Bootstrap: los modales clásicos de estos listados */
.modal-backdrop.fade.show {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background-color: var(--modal-overlay);
  backdrop-filter: blur(4px);
}

.modal.show .modal-dialog {
  z-index: 1060;
}

.modal-content {
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  background: var(--bg-surface);
  box-shadow: var(--shadow-lg);
}

/* Los modales abiertos a mano (sin el JS de Bootstrap) necesitan esto */
.modal.modal-open-fix,
.modal.fade.show {
  display: block;
  pointer-events: auto;
}

body.modal-open {
  overflow: hidden;
}

/* Con un diálogo abierto, el menú lateral y la barra superior se APAGAN del
   todo en vez de quedarse medio visibles bajo el oscurecido: a media luz se ve
   sucio y compite con la ventana. No se sacan del flujo (nada de \`display:none\`)
   porque la ficha está maquetada para ese ancho: solo se apaga su opacidad, así
   que el alto y el ancho no cambian y los formularios no se descuadran. */
body.modal-open :is(.sidebar, .navbar-custom) {
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
}

/* ═══════════════════════════════════════════════════════════════════════════
   9. Estados vacíos y de carga
   ═══════════════════════════════════════════════════════════════════════════ */

:is(.list-loading, .list-empty, .wol-loading, .wol-empty, .pel-loading, .pel-empty,
    .servers-empty-state, .locations-empty-state, .racks-empty-state) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 3.5rem 2rem;
  border: 1px dashed var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-toolbar);
  color: var(--text-muted);
  font-size: 0.95rem;
  text-align: center;
}

:is(.list-empty, .wol-empty, .pel-empty, .servers-empty-state,
    .locations-empty-state, .racks-empty-state) :is(span, .empty-icon, .pel-empty-icon) {
  font-size: 2.25rem;
  opacity: 0.8;
}

:is(.empty-title, .list-empty-title) {
  margin: 0;
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
}

:is(.empty-subtitle, .list-empty-subtitle) {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* ═══════════════════════════════════════════════════════════════════════════
   10. Detalles globales
   ═══════════════════════════════════════════════════════════════════════════ */

button:focus-visible,
a:focus-visible,
:is(.list-column-item, .wol-column-item, .column-menu-item):focus-visible {
  outline: 2px solid rgba(var(--accent-rgb), 0.7);
  outline-offset: 2px;
  box-shadow: none;
}

.btn-primary .btn-icon-left {
  margin-right: 0.35rem;
}

@keyframes list-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes list-slide-down {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════════════════════════════════════════
   11. Pantallas pequeñas: la tabla pasa a fichas apiladas
   ═══════════════════════════════════════════════════════════════════════════ */

@media (max-width: 992px) {
  :is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap) {
    max-height: calc(100vh - 260px);
  }

  :is(.list-toolbar, .wol-filters, .pel-filters) {
    align-items: stretch;
    flex-direction: column;
  }

  :is(.list-toolbar-right, .wol-inline-list-controls) {
    margin-left: 0;
  }

  :is(.list-toolbar-right, .wol-inline-list-controls) button {
    width: 100%;
  }
}

@media (max-width: 768px) {
  :is(.list-head, .wol-header, .pel-header) {
    align-items: stretch;
    flex-direction: column;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead {
    display: none;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table),
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody,
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr,
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td {
    display: block;
    width: 100%;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr {
    margin-bottom: 0.75rem;
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    background: var(--bg-card);
    overflow: hidden;
  }

  /* Cada fila se lee como una ficha: etiqueta a la izquierda y valor a la
     derecha. Antes cada celda ocupaba una línea entera con su etiqueta encima,
     así que un listado de 12 columnas medía media pantalla por registro. */
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.38rem 0.85rem;
    border-bottom: 1px solid var(--border);
    background-color: transparent !important;
    text-align: right;
    min-width: 0;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td[data-label]::before {
    content: attr(data-label);
    flex: 0 1 auto;
    margin: 0;
    font-size: 0.68rem;
    font-weight: var(--fw-bold);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    text-align: left;
  }

  /* La primera celda es el nombre del registro: sin etiqueta y a la izquierda. */
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td:first-child:not(.list-select-cell) {
    display: block;
    padding: 0.7rem 0.85rem 0.45rem;
    text-align: left;
    font-weight: var(--fw-semibold);
    color: var(--text-primary);
    font-size: 0.92rem;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td:first-child:not(.list-select-cell)::before {
    display: none;
  }

  /* Con selección múltiple la primera celda es la casilla: el nombre es la
     siguiente y tiene que seguir leyéndose como título de la ficha. */
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell + td {
    display: block;
    padding: 0.35rem 0.85rem 0.45rem;
    text-align: left;
    font-weight: var(--fw-semibold);
    color: var(--text-primary);
    font-size: 0.92rem;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell + td::before {
    display: none;
  }

  /* Sin datos no se pinta la línea: sería una fila de «—» por columna. */
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-cell--empty {
    display: none;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr:hover td {
    background-color: transparent !important;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td:last-child {
    border-bottom: none;
  }

  /* Acciones: botones alineados a la izquierda y con área de dedo. */
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr > td:last-child:not([data-label]) {
    display: flex;
    justify-content: flex-start;
    gap: 0.5rem;
    padding: 0.5rem 0.85rem 0.6rem;
  }

  :is(.list-actions, .wol-actions, .pel-actions, .servers-actions, .locations-actions, .racks-actions) {
    justify-content: flex-start;
    gap: 0.5rem;
  }

  :is(.list-action-btn, .list-icon-btn, .wol-action-btn, .wol-btn-sm,
      .wol-btn-sm-edit, .wol-btn-sm-delete, .pel-action-btn) {
    width: 36px;
    height: 36px;
  }

  /* La celda de selección múltiple se queda arriba, junto al nombre. */
  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell {
    display: flex;
    justify-content: flex-start;
    padding: 0.6rem 0.85rem 0;
    border-bottom: 0;
  }

  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell input {
    width: 20px;
    height: 20px;
  }

  :is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap) {
    max-height: none;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   Rueda dentada de columnas
   Vive al final de la CABECERA de la tabla (no en la barra de herramientas: el
   botón con texto «Columnas» ocupaba sitio útil ahí). El desplegable se pinta
   fuera de la tabla con \`position: fixed\` (ver \`ListPage\`), porque la tabla
   hace scroll y un menú absoluto dentro del \`thead\` quedaría recortado.
   ══════════════════════════════════════════════════════════════════════════ */
.list-th-cog {
  width: 42px;
  text-align: right;
  padding-right: 12px !important;
  cursor: default;
}

.list-cog-btn {
  background: none;
  border: 0;
  padding: 2px;
  margin: 0;
  line-height: 1;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 6px;
}

.list-cog-btn:hover {
  color: var(--text-primary);
  background: var(--bg-toolbar);
}

.list-cog-btn svg {
  width: 15px;
  height: 15px;
}


/* ═══════════════════════════════════════════════════════════════════════════
   18. Modal de «Documentos vinculados» (DocSmartButton)

   Las cinco acciones de este modal estaban en el pie como botones grandes
   (\`list-btn-secondary\`, 0.5rem de relleno): con cuatro de ellos el texto partía
   en dos líneas y la fila entera quedaba pegada a la lista. Ahora las cuatro
   acciones de creación viven en una barra propia dentro del cuerpo, en pequeño,
   y el pie se queda solo con Cerrar.
   ═══════════════════════════════════════════════════════════════════════════ */

.doc-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.875rem;
  padding-top: 0.875rem;
  border-top: 1px solid var(--border);
}

/* Chip de acción: el \`list-chip-btn\` de la casa, pero con icono y sin partirse. */
.doc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.doc-chip svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.doc-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Renglón de «quién y cuándo» bajo el título del documento. */
.doc-row-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  margin-top: 3px;
  font-size: 0.72rem;
  color: var(--text-muted);
  line-height: 1.35;
}

.doc-row-meta strong {
  font-weight: var(--fw-semibold);
  color: var(--text-secondary);
}
/* Traza del documento en el editor (pestaña Meta) y filas del historial. */
.doc-meta-traza {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--bg-toolbar);
}

.doc-meta-traza p {
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.45;
}

.doc-meta-traza strong {
  font-weight: var(--fw-semibold);
  color: var(--text-secondary);
}

.doc-link-btn {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: var(--accent-l);
  cursor: pointer;
  text-decoration: underline;
}

.doc-hist {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.doc-hist-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--bg-toolbar);
}

.doc-hist-head {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
}

.doc-hist-ver {
  font-size: 0.82rem;
  font-weight: var(--fw-bold);
  color: var(--text-primary);
}
/* El pie del modal de documentos respira: los chips de acción ya llevan su
   separador y el botón Cerrar salía pegado a ellos. */
.doc-modal .list-dialog-actions {
  margin-top: 1rem;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Alta masiva de tomas de pared — tabla editable dentro del diálogo
   ═══════════════════════════════════════════════════════════════════════════ */

/* Fila de campos de la «semilla» y del generador de códigos. */
.bulk-field-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
}

.bulk-field-row .bulk-field {
  margin: 0;
  flex: 1 1 140px;
  min-width: 120px;
}

.bulk-field-row .list-input {
  width: 100%;
}

/* La tabla puede ser larga (48 tomas de un edificio): se desplaza ella sola,
   no el diálogo entero, para que la cabecera y los botones sigan a la vista. */
.bulk-grid-wrap {
  margin-top: 10px;
  max-height: 44vh;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.bulk-grid {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.bulk-grid thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 0.45rem 0.5rem;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);
  text-align: left;
  white-space: nowrap;
  font-weight: var(--fw-semibold);
  color: var(--text-secondary);
}

.bulk-grid thead th.sortable { cursor: pointer; -webkit-user-select: none; user-select: none; }
.bulk-grid thead th.sortable:hover { color: var(--text-primary); }

.bulk-grid td {
  padding: 0.2rem 0.3rem;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

/* Fila con un código que el backend ha rechazado. */
.bulk-grid tr.is-error td { background: rgba(239, 68, 68, 0.08); }

/* Los campos de una celda van justos: el \`min-width: 200px\` de la regla
   general de listados ensancharía la tabla hasta hacerla inservible. */
.bulk-grid :is(input, select) {
  width: 100%;
  min-width: 0;
  padding: 0.32rem 0.45rem;
  font-size: 0.82rem;
}

.bulk-grid td:first-child input { min-width: 96px; font-family: monospace; }

.bulk-grid-error {
  margin: 2px 0 0;
  font-size: 0.72rem;
  color: var(--red-600);
}

/* ── Diálogo de alta masiva: más ancho y con campos más grandes ─────────────
   Cabe la cadena completa (panel + switch) sin que la tabla quede apretada:
   con 11 columnas y letra de 0,82rem había que hacer zoom para trabajar. */
.bulk-dialog {
  display: flex;
  flex-direction: column;
  padding: 1.75rem;
  max-height: 94vh;
  overflow-y: auto;
}

.bulk-dialog h4 {
  font-size: 1.15rem;
  margin-bottom: 0.85rem;
}

.bulk-dialog .bulk-field > span {
  font-size: 0.9rem;
}

.bulk-dialog .bulk-field-row {
  gap: 12px;
}

.bulk-dialog .bulk-field-row .bulk-field {
  flex: 1 1 170px;
  min-width: 150px;
}

.bulk-dialog .bulk-field-row .bulk-field--switches {
  flex: 2 1 300px;
}

.bulk-dialog .list-input,
.bulk-dialog .list-select {
  padding: 0.55rem 0.7rem;
  font-size: 0.95rem;
}

.bulk-dialog .bulk-grid {
  font-size: 0.95rem;
}

.bulk-dialog .bulk-grid thead th {
  padding: 0.6rem 0.55rem;
  font-size: 0.88rem;
}

.bulk-dialog .bulk-grid td {
  padding: 0.3rem 0.35rem;
}

.bulk-dialog .bulk-grid :is(input, select) {
  padding: 0.46rem 0.55rem;
  font-size: 0.95rem;
}

.bulk-dialog .bulk-grid-wrap {
  flex: 1 1 auto;
  min-height: 180px;
  max-height: none;
}

.bulk-dialog .list-dialog-actions {
  flex: 0 0 auto;
  margin-top: 14px;
}

/* Selector múltiple de switches de la semilla: chips + desplegable. */
.bulk-switch-picker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.bulk-switch-picker .list-select {
  flex: 1 1 180px;
  min-width: 160px;
}

.bulk-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 10px;
  border: 1px solid rgba(var(--accent-rgb), 0.35);
  border-radius: 999px;
  background: rgba(var(--accent-rgb), 0.14);
  color: var(--text-primary);
  font-size: 0.85rem;
}

.bulk-chip button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
}

.bulk-chip button:hover {
  color: var(--red-600);
  background: rgba(239, 68, 68, 0.12);
}

/* ═══════════════════════════════════════════════════════════════════════════
   12. Pantallas BAJAS (portátil de 14": 1366×768 y, con escalado al 125 %,
       1092×614). Aquí el problema no es el ancho sino el alto: cada franja de
       aire se paga en filas que no se ven. Se recorta lo decorativo y la tabla
       se queda con el alto que sobre. En pantallas altas no cambia nada.
   ═══════════════════════════════════════════════════════════════════════════ */
@media (max-height: 820px) {
  :is(.list-page, .wol-container, .pel-container) {
    padding-bottom: 0.75rem;
  }

  :is(.list-head, .wol-header, .pel-header) {
    gap: 0.6rem;
    margin-bottom: 0.8rem;
  }

  /* Fichas embebidas (con pestañas de sección): la línea separadora ya separa. */
  .list-page--embedded > .list-head {
    margin-bottom: 0.6rem;
    padding-bottom: 0.4rem;
  }

  :is(.list-subtitle, .wol-subtitle, .pel-subtitle) {
    margin-top: 0.1rem;
    font-size: 0.8rem;
  }

  :is(.list-toolbar, .wol-filters, .pel-filters) {
    margin-bottom: 0.6rem;
    padding: 0.45rem 0.55rem;
  }

  /* La tabla ocupa lo que queda del viewport en vez de dejar un hueco muerto.
     Con la cabecera de una fila el chrome baja a ~196 px: 250 dejaría ~35 px
     sin usar; 215 los aprovecha (si se abren los filtros, la página scrollea). */
  :is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap) {
    max-height: calc(100dvh - 215px);
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   13. Densidad de las tablas → \`styles/density.css\`
       (va después de \`skins.css\` porque tiene que ganar al relleno del skin;
       ver \`theme/density.js\` para el valor y los umbrales).
   ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════
   14. Cabecera de UNA fila en pantalla baja (\`.list-page--compact\`)
       La pone \`ListPage\` cuando \`useCompactViewport()\` dice que la ventana es
       baja o estrecha. El buscador y el botón de filtros suben a la cabecera
       (\`.list-head-search\`), la barra de herramientas desaparece y la cabecera
       se aprieta: icono fuera, subtítulo en la misma línea que el título y
       botones más pequeños. Ganancia real en un 14": ~50 px de alto.
   ═══════════════════════════════════════════════════════════════════════════ */
.list-head-search {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 1 1 200px;
  max-width: 340px;
  min-width: 0;
}

.list-search--inline {
  width: 100%;
  min-width: 0;
  padding: 0.4rem 0.7rem;
  font-size: 0.82rem;
}

.list-page--compact :is(.list-head, .wol-header) {
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.list-page--compact .list-head-icon {
  display: none;
}

/* Título y contador en la misma línea: la cabecera pasa de dos renglones a uno. */
.list-page--compact .list-head-left > div {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 0.55rem;
  min-width: 0;
}

.list-page--compact :is(.list-title, .list-section-title) {
  font-size: 1.1rem;
}

.list-page--compact :is(.list-subtitle, .list-section-subtitle) {
  margin: 0;
  max-width: 46ch;
  font-size: 0.76rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-page--compact .list-head-actions,
.list-page--compact .list-head-search {
  gap: 0.35rem;
}

/* Botones de la cabecera y de la barra: un punto más pequeños. */
.list-page--compact :is(.list-head-actions, .list-toolbar) :is(.list-btn-primary, .list-btn-secondary) {
  padding: 0.4rem 0.7rem;
  font-size: 0.82rem;
}

/* El conmutador de vistas se queda en iconos (el nombre ya va en el title). */
.list-page--compact .list-view-btn {
  padding: 0.35rem 0.45rem;
}

.list-page--compact .list-view-btn-label {
  display: none;
}

/* ═══════════════════════════════════════════════════════════════════════════
   15. Tabla EDITABLE dentro de un listado (vista «Alta rápida» de tomas)
       Una vista propia pinta una tabla con celdas de formulario. Se reutilizan
       las clases \`list-table\` / \`list-table-wrap\` para que mida y se comporte
       igual que la tabla normal; aquí solo van las piezas de edición.
   ═══════════════════════════════════════════════════════════════════════════ */
.list-grid-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.list-grid-spacer {
  flex: 1 1;
}

/* Acciones a la derecha; el grupo no se parte aunque la barra haga wrap. */
.list-grid-actions {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-left: auto;
}

.list-grid-title {
  font-size: 0.9rem;
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
  white-space: nowrap;
}

.list-table--editable td {
  vertical-align: top;
}

/* Celda de formulario: ocupa el ancho de la columna y no impone el min-width
   de 200 px de \`.list-input\` (la tabla tiene 9 columnas). */
.list-cell-input,
.list-cell-select {
  width: 100%;
  min-width: 88px;
  padding: 0.3rem 0.45rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.82rem;
}

.list-cell-select {
  min-width: 118px;
  cursor: pointer;
}

.list-cell-input--short,
.list-cell-select--short {
  min-width: 72px;
}

.list-cell-input:focus,
.list-cell-select:focus {
  outline: none;
  border-color: rgba(var(--accent-rgb), 0.55);
  box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.15);
}

.list-cell-input:disabled,
.list-cell-select:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Fila nueva: se distingue de las existentes (que van en gris). */
.list-row--new td {
  background: rgba(var(--accent-rgb), 0.07) !important;
}

.list-row--new td:first-child {
  box-shadow: inset 3px 0 0 var(--accent);
}

/* Fila con error del backend o código repetido. */
.list-table--editable tr.is-error td {
  background: rgba(220, 38, 38, 0.12) !important;
}

.list-cell-error {
  display: block;
  margin-top: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--red-600);
}

/* Tomas ya creadas: editables en línea (código, nombre, sala, planta, tipo). */
.list-row--locked {
  cursor: default;
}

/* Fila con cambios sin guardar: se marca con el acento y una banda a la izquierda. */
.list-row--locked.is-dirty td {
  background: rgba(var(--accent-rgb), 0.06) !important;
}

.list-row--locked.is-dirty td:first-child {
  box-shadow: inset 3px 0 0 var(--accent);
}

/* ── Modo «hoja de cálculo» (pantalla completa) ────────────────────────────
   Cubre la aplicación entera (menú lateral y barra superior incluidos) y el
   alto lo consume la tabla, con la cabecera de columnas pegada arriba. En un
   14" son ~70 px más de filas y desaparece todo lo que distrae. */
.list-grid {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.list-grid--full {
  position: fixed;
  inset: 0;
  z-index: 1200;
  padding: 0.75rem 1rem 1rem;
  background: var(--bg-surface);
}

.list-grid--full .list-table-wrap {
  flex: 1 1;
  min-height: 0;
  max-height: none;
  overflow: auto;
}

.list-grid--full .list-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bg-toolbar);
}

.list-grid--full .list-grid-bar {
  margin-bottom: 0.6rem;
}
/* ── Columnas arrastrables desde la cabecera (2026-10-30) ──────────────────
   \`ListPage\` marca cada \`th\` con \`draggable\`. Aquí solo se le dice al usuario
   que se puede mover y dónde va a caer: asa con el cursor y una línea vertical
   en la columna destino. */
.list-table thead th.list-th-draggable {
  cursor: grab;
  -webkit-user-select: none;
          user-select: none;
}

.list-table thead th.list-th-draggable:active {
  cursor: grabbing;
}

.list-table thead th.list-th-draggable.is-dragging {
  opacity: 0.45;
}

.list-table thead th.list-th-draggable.is-drop-target {
  box-shadow: inset 3px 0 0 var(--accent);
  background: rgba(var(--accent-rgb, 59, 130, 246), 0.08);
}

/* ── Edición masiva de campos (BulkEditAction) ───────────────────────────────
   Cada campo es una fila: casilla + nombre a la izquierda y el editor a la
   derecha (solo cuando la casilla está marcada). Las filas marcadas llevan
   fondo para que se vea de un vistazo qué se va a cambiar. */
.bulk-edit-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
  max-height: 46vh;
  overflow-y: auto;
  padding-right: 2px;
}

.bulk-edit-field {
  display: grid;
  grid-template-columns: minmax(140px, 210px) 1fr;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
}

.bulk-edit-field.is-on {
  border-color: var(--border);
  background: var(--bg-toolbar);
}

.bulk-edit-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: var(--fw-semibold);
  color: var(--text-secondary);
}

.bulk-edit-check input {
  accent-color: var(--accent);
}

.bulk-edit-field.is-on .bulk-edit-check {
  color: var(--text-primary);
}

.bulk-edit-field :is(.list-input, .list-select) {
  width: 100%;
  min-width: 0;
}

.bulk-edit-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: var(--text-secondary);
}

.bulk-edit-switch input {
  accent-color: var(--accent);
}

.bulk-edit-dialog h4 {
  margin-bottom: 0.5rem;
}
`, "",{"version":3,"sources":["webpack://./src/styles/lists.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;EAyCE;;AAEF;;gFAEgF;;AAEhF;EACE,iBAAiB;EACjB,oEAAoE;EACpE,iBAAiB;EACjB,iCAAiC;AACnC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,eAAe;EACf,SAAS;EACT,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,YAAY;AACd;;AAEA;0DAC0D;AAC1D;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,WAAW;AACb;;AAEA;EACE,cAAc;EACd,eAAe;EACf,cAAc;EACd,oBAAoB;EACpB,aAAa;AACf;;AAEA;EACE,SAAS;EACT,iBAAiB;EACjB,2BAA2B;EAC3B,wBAAwB;EACxB,gBAAgB;EAChB,0BAA0B;AAC5B;;AAEA;EACE,kBAAkB;EAClB,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA;;;;qEAIqE;AACrE;EACE,qBAAqB;EACrB,mBAAmB;EACnB,uBAAuB;EACvB,sCAAsC;AACxC;;AAEA;EACE,SAAS;EACT,kBAAkB;EAClB,+BAA+B;EAC/B,uBAAuB;EACvB,iBAAiB;EACjB,0BAA0B;AAC5B;;AAEA;EACE,mBAAmB;EACnB,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,cAAc;EACd,aAAa;AACf;;AAEA,6DAA6D;AAC7D;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,uBAAuB;EACvB,qBAAqB;EACrB,SAAS;EACT,+BAA+B;EAC/B,gBAAgB;EAChB,4BAA4B;EAC5B,iBAAiB;EACjB,eAAe;EACf,yDAAyD;AAC3D;;AAEA;EACE,6BAA6B;EAC7B,0BAA0B;AAC5B;;AAEA;;gFAEgF;;AAEhF;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,YAAY;EACZ,sBAAsB;EACtB,mBAAmB;EACnB,+BAA+B;EAC/B,+BAA+B;EAC/B,6BAA6B;AAC/B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,WAAW;AACb;;AAEA;yDACyD;AACzD;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,WAAW;EACX,YAAY;AACd;;AAEA;EACE,aAAa;AACf;;AAEA,iFAAiF;AACjF;EACE,mBAAmB;AACrB;;AAEA;EACE,0CAA0C;EAC1C,sBAAsB;AACxB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,YAAY;EACZ,cAAc;EACd,kBAAkB;EAClB,yBAAyB;EACzB,WAAW;EACX,iBAAiB;EACjB,2BAA2B;EAC3B,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,WAAW;EACX,iBAAiB;AACnB;;AAEA;EACE,kBAAkB;EAClB,2BAA2B;EAC3B,yBAAyB;EACzB,sBAAsB;EACtB,wBAAwB;AAC1B;;AAEA;;;+CAG+C;AAC/C;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,+BAA+B;EAC/B,2DAA2D;EAC3D,2BAA2B;AAC7B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,sBAAsB;EACtB,SAAS;EACT,uEAAuE;EACvE,uBAAuB;EACvB,4BAA4B;EAC5B,oBAAoB;EACpB,iBAAiB;EACjB,+BAA+B;EAC/B,cAAc;EACd,mBAAmB;EACnB,eAAe;EACf,yDAAyD;AAC3D;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;EACd,iBAAiB;AACnB;;AAEA;EACE,yCAAyC;EACzC,0BAA0B;AAC5B;;AAEA;EACE,yBAAyB;EACzB,WAAW;AACb;;AAEA;EACE,uBAAuB;EACvB,gBAAgB;EAChB,oCAAoC;EACpC;8DAC4D;EAC5D,wBAAwB;AAC1B;;AAEA,4EAA4E;AAC5E;EACE;IACE,aAAa;EACf;AACF;;AAEA,gEAAgE;AAChE;EACE,iCAAiC;EACjC;kFACgF;EAChF,YAAY;AACd;;AAEA,gFAAgF;AAChF;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,UAAU;EACV,SAAS;EACT,+BAA+B;EAC/B,uBAAuB;EACvB,wBAAwB;EACxB,eAAe;EACf,cAAc;EACd,yDAAyD;AAC3D;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iBAAiB;AACnB;;AAEA;EACE,yCAAyC;EACzC,sBAAsB;AACxB;;AAEA,gFAAgF;AAChF;EACE,qBAAqB;EACrB,WAAW;EACX,cAAc;AAChB;;AAEA,iEAAiE;AACjE;EACE,aAAa;EACb,4DAA4D;EAC5D,WAAW;AACb;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX,oBAAoB;EACpB,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,4BAA4B;EAC5B,eAAe;EACf,gFAAgF;AAClF;;AAEA;EACE,2CAA2C;EAC3C,4BAA4B;EAC5B,2BAA2B;AAC7B;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,8BAA8B;EAC9B,WAAW;AACb;;AAEA;EACE,kBAAkB;EAClB,2BAA2B;EAC3B,0BAA0B;EAC1B,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,oBAAoB;EACpB,iBAAiB;EACjB,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,QAAQ;EACR,gBAAgB;AAClB;;AAEA,+EAA+E;AAC/E;EACE,oBAAoB;EACpB,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,SAAO;EACP,WAAW;EACX,kBAAkB;EAClB,4BAA4B;EAC5B,gBAAgB;AAClB;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,2BAA2B;AAC7B;;AAEA,+EAA+E;AAC/E;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,WAAW;EACX,WAAW;EACX,uBAAuB;EACvB,SAAS;EACT,+BAA+B;EAC/B,6BAA6B;EAC7B,0BAA0B;EAC1B,oBAAoB;EACpB,kBAAkB;EAClB,gBAAgB;EAChB,eAAe;EACf,uCAAuC;AACzC;;AAEA;EACE,yCAAyC;AAC3C;;AAEA;EACE,+BAA+B;EAC/B,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA,+EAA+E;AAC/E;EACE,aAAa;EACb,WAAW;EACX,uBAAuB;EACvB,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;EAC/B,6BAA6B;AAC/B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,WAAW;EACX,uBAAuB;EACvB,sCAAsC;EACtC,kBAAkB;EAClB,2BAA2B;EAC3B,0BAA0B;AAC5B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX,gBAAgB;EAChB,+BAA+B;EAC/B,gBAAgB;AAClB;;AAEA,0EAA0E;AAC1E;EACE,qDAAqD;AACvD;;AAEA;EACE,mCAAmC;EACnC,oBAAoB;AACtB;;AAEA;;6CAE6C;AAC7C;EACE,0CAA0C;AAC5C;;AAEA;EACE,mDAAmD;AACrD;;AAEA;EACE,yCAAyC;AAC3C;;AAEA;EACE,iDAAiD;AACnD;;AAEA;0EAC0E;AAC1E;EACE,6BAA6B;EAC7B,mCAAmC;EACnC,wBAAwB;AAC1B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,YAAY;EACZ,gBAAgB;EAChB,YAAY;EACZ,UAAU;EACV,0BAA0B;EAC1B,kBAAkB;EAClB,gBAAgB;EAChB,eAAe;AACjB;;AAEA;EACE,wBAAwB;EACxB,gBAAgB;AAClB;;AAEA;;;8CAG8C;AAC9C;;EAEE,WAAW;EACX,kBAAkB;EAClB,6BAA6B;EAC7B,2BAA2B;AAC7B;;AAEA;;EAEE,WAAW;EACX,YAAY;EACZ,SAAS;EACT,2BAA2B;EAC3B,eAAe;EACf,sBAAsB;AACxB;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,eAAe;EACf,mBAAmB;EACnB,iBAAiB;EACjB,+CAA+C;EAC/C,2DAA2D;EAC3D,yCAAyC;AAC3C;;AAEA;EACE,kBAAkB;EAClB,0BAA0B;AAC5B;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;AACjB;;AAEA;EACE,iBAAiB;AACnB;;AAEA,4CAA4C;AAC5C;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,4BAA4B;AAC9B;;AAEA;EACE,WAAW;AACb;;AAEA;EACE,gBAAgB;AAClB;;AAEA,4DAA4D;AAC5D;;;EAGE,gBAAgB;EAChB,uBAAuB;EACvB,+BAA+B;EAC/B,2DAA2D;EAC3D,2BAA2B;EAC3B,0BAA0B;EAC1B,oBAAoB;EACpB,mBAAmB;EACnB,0DAA0D;AAC5D;;AAEA;EACE,SAAO;AACT;;AAEA;EACE,eAAe;EACf,mBAAmB;EACnB,gBAAgB;EAChB,wBAAwB;EACxB,kFAAkF;EAClF;8DAC4D;EAC5D,8DAA8D;EAC9D,iCAAiC;EACjC,4BAA4B;AAC9B;;AAEA;;EAEE,wBAAwB;AAC1B;;AAEA;;EAEE,aAAa;EACb,2CAA2C;EAC3C,2EAA2E;AAC7E;;AAEA;EACE,6BAA6B;EAC7B,0BAA0B;AAC5B;;AAEA,qEAAqE;AACrE;EACE,aAAa;EACb,2DAA2D;EAC3D,YAAY;EACZ,sBAAsB;AACxB;;AAEA;EACE,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;AAC5B;;AAEA;EACE,iBAAiB;EACjB,2BAA2B;EAC3B,gBAAgB;EAChB,0BAA0B;AAC5B;;AAEA;EACE,kBAAkB;EAClB,kBAAkB;EAClB,2BAA2B;EAC3B,yBAAyB;EACzB,sBAAsB;EACtB,wBAAwB;AAC1B;;AAEA;gEACgE;AAChE;EACE,aAAa;EACb,eAAe;EACf,oBAAoB;EACpB,WAAW;AACb;;AAEA;EACE,oBAAoB;EACpB,sBAAsB;EACtB,uBAAuB;EACvB,WAAW;EACX,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,2BAA2B;EAC3B,6BAA6B;EAC7B,yCAAyC;AAC3C;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,iBAAiB;EACjB,iBAAiB;EACjB,2BAA2B;EAC3B,yBAAyB;EACzB,sBAAsB;EACtB,wBAAwB;AAC1B;;AAEA;;gFAEgF;;AAEhF;;EAEE,cAAc;EACd,iCAAiC;EACjC,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,4BAA4B;AAC9B;;AAEA,gFAAgF;AAChF;EACE,WAAW;EACX,yBAAyB;EACzB,iBAAiB;EACjB,0BAA0B;AAC5B;;AAEA;EACE,gBAAgB;EAChB,MAAM;EACN,UAAU;EACV,kBAAkB;EAClB,sCAAsC;EACtC;;uEAEqE;EACrE,6CAA6C;EAC7C,wBAAwB;EACxB,iBAAiB;EACjB,2BAA2B;EAC3B,yBAAyB;EACzB,sBAAsB;EACtB,gBAAgB;EAChB;;gEAE8D;EAC9D,mBAAmB;EACnB,gBAAgB;EAChB,sBAAsB;AACxB;;AAEA;EACE,kBAAkB;EAClB,sCAAsC;EACtC,aAAa;EACb,sBAAsB;EACtB,0BAA0B;EAC1B,gBAAgB;AAClB;;AAEA;EACE,gBAAgB;AAClB;;AAEA,4DAA4D;AAC5D;EACE,6BAA6B;AAC/B;;AAEA;EACE,2DAA2D;AAC7D;;AAEA;EACE,sEAAsE;AACxE;;AAEA,iFAAiF;AACjF;EACE,wCAAwC;AAC1C;;AAEA;EACE,sEAAsE;AACxE;;AAEA;EACE,eAAe;EACf,yBAAiB;UAAjB,iBAAiB;AACnB;;AAEA;EACE,wCAAwC;EACxC,4BAA4B;AAC9B;;AAEA;EACE,oBAAoB;EACpB,iBAAiB;EACjB,2BAA2B;EAC3B,oBAAoB;AACtB;;AAEA;EACE,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,+CAA+C;EAC/C,oBAAoB;AACtB;;AAEA,2BAA2B;AAC3B;EACE,uBAAuB;EACvB,wCAAwC;EACxC,sCAAsC;AACxC;;AAEA;EACE,UAAU;EACV,SAAS;EACT,uBAAuB;EACvB,0BAA0B;EAC1B,oBAAoB;EACpB,iBAAiB;EACjB,+BAA+B;EAC/B,eAAe;AACjB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;;gFAEgF;;AAEhF;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,yCAAyC;EACzC,sBAAsB;EACtB,6BAA6B;EAC7B,oBAAoB;AACtB;;AAEA;EACE,6BAA6B;EAC7B,iBAAiB;EACjB,4BAA4B;AAC9B;;AAEA,0EAA0E;AAC1E,sCAAsC,uBAAuB,EAAE,oBAAoB,EAAE;AACrF,iDAAiD,wBAAwB,EAAE,oBAAoB,EAAE;AACjG,qCAAqC,wBAAwB,EAAE,kBAAkB,EAAE,gBAAgB,EAAE;;AAErG;EACE,qBAAqB;EACrB,gBAAgB;EAChB,4CAA4C;EAC5C,kBAAkB;EAClB,+BAA+B;EAC/B,sBAAsB;AACxB;;AAEA;EACE,mCAAmC;EACnC,uBAAuB;AACzB;;AAEA;EACE,mCAAmC;EACnC,qBAAqB;AACvB;;AAEA;EACE,mCAAmC;EACnC,uBAAuB;AACzB;;AAEA;;8DAE8D;AAC9D;EACE,yCAAyC;EACzC,sBAAsB;AACxB;;AAEA;EACE,6BAA6B;EAC7B,4BAA4B;EAC5B,+BAA+B;AACjC;;AAEA;EACE,iBAAiB;EACjB,wBAAwB;AAC1B;;AAEA;;gFAEgF;;AAEhF;EACE,aAAa;EACb,mBAAmB;EACnB,yBAAyB;EACzB,QAAQ;AACV;;AAEA;EACE,iBAAiB;EACjB,SAAS;EACT,mBAAmB;AACrB;;AAEA;;;EAGE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,UAAU;EACV,+BAA+B;EAC/B,8CAA8C;EAC9C,2BAA2B;EAC3B,4BAA4B;EAC5B,YAAY;EACZ,cAAc;EACd,eAAe;EACf,cAAc;EACd,kFAAkF;AACpF;;AAEA;;EAEE,WAAW;EACX,YAAY;EACZ,iBAAiB;AACnB;;AAEA;;EAEE,yCAAyC;EACzC,0CAA0C;EAC1C,sBAAsB;AACxB;;AAEA;;EAEE,mCAAmC;EACnC,qCAAqC;EACrC,qBAAqB;AACvB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;;gFAEgF;;AAEhF;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,oBAAoB;EACpB,SAAS;EACT,2DAA2D;EAC3D,qDAAqD;EACrD,WAAW;EACX,oBAAoB;EACpB,mBAAmB;EACnB,+BAA+B;EAC/B,mBAAmB;EACnB,eAAe;EACf,0EAA0E;AAC5E;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,wBAAwB;EACxB,yDAAyD;EACzD,2BAA2B;AAC7B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,oBAAoB;EACpB,+BAA+B;EAC/B,2DAA2D;EAC3D,6BAA6B;EAC7B,4BAA4B;EAC5B,oBAAoB;EACpB,mBAAmB;EACnB,eAAe;EACf,kFAAkF;AACpF;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,2CAA2C;EAC3C,0BAA0B;AAC5B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,oBAAoB;EACpB,yCAAyC;EACzC,2DAA2D;EAC3D,mCAAmC;EACnC,qBAAqB;EACrB,oBAAoB;EACpB,mBAAmB;EACnB,eAAe;EACf,gEAAgE;AAClE;;AAEA;EACE,kCAAkC;EAClC,qCAAqC;AACvC;;AAEA;;;EAGE,YAAY;EACZ,mBAAmB;EACnB,eAAe;EACf,gBAAgB;AAClB;;AAEA,iEAAiE;AACjE;EACE,uBAAuB;EACvB,+BAA+B;EAC/B,wDAAwD;EACxD,6BAA6B;EAC7B,4BAA4B;EAC5B,oBAAoB;EACpB,kBAAkB;EAClB,+BAA+B;EAC/B,eAAe;EACf,kFAAkF;AACpF;;AAEA;;EAEE,yCAAyC;EACzC,0CAA0C;EAC1C,sBAAsB;AACxB;;AAEA;;gFAEgF;;AAEhF;EACE,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,QAAQ;EACR,WAAW;EACX,kBAAkB;EAClB,eAAe;EACf,gBAAgB;EAChB,iBAAiB;EACjB,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;EAC/B;gEAC8D;EAC9D,6CAA6C;EAC7C,4BAA4B;EAC5B,qCAAqC;AACvC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,+BAA+B;EAC/B,4BAA4B;EAC5B,kBAAkB;EAClB,eAAe;EACf,yBAAiB;UAAjB,iBAAiB;EACjB,yDAAyD;AAC3D;;AAEA;EACE,8CAA8C;EAC9C,0BAA0B;AAC5B;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,qBAAqB;EACrB,2BAA2B;EAC3B,eAAe;AACjB;;AAEA;;gFAEgF;;AAEhF;EACE,eAAe;EACf,QAAQ;EACR;;;wDAGsD;EACtD,aAAa;EACb,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,eAAe;EACf,+BAA+B;EAC/B,+BAA+B;EAC/B;wCACsC;EACtC,6CAA6C;EAC7C,4BAA4B;AAC9B;;AAEA;EACE,kBAAkB;EAClB,eAAe;EACf,2BAA2B;EAC3B,0BAA0B;AAC5B;;AAEA;;;kEAGkE;AAClE;EACE,aAAa;EACb,sBAAsB;EACtB,gBAAgB;AAClB;;AAEA;EACE,UAAU;EACV,aAAa;EACb,mBAAmB;EACnB,QAAQ;AACV;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,UAAU;EACV,oBAAoB;AACtB;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;EACE,cAAc;EACd,aAAa;EACb,gBAAgB;EAChB,kBAAkB;EAClB,kEAAkE;EAClE,kBAAkB;AACpB;;AAEA;EACE,UAAU;EACV,gBAAgB;EAChB,oBAAoB;EACpB,mCAAmC;AACrC;;AAEA;EACE,mBAAmB;EACnB,mBAAmB;EACnB,gBAAgB;EAChB,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,YAAY;AACd;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;EAC/B,6BAA6B;EAC7B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,qBAAqB;EACrB,sCAAsC;EACtC,uBAAuB;AACzB;;AAEA;EACE,SAAO;EACP,SAAS;EACT,eAAe;EACf,+BAA+B;EAC/B,0BAA0B;AAC5B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,aAAa;EACb,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,YAAY;AACd;;AAEA;EACE,aAAa;EACb,SAAO;EACP,sBAAsB;EACtB,WAAW;EACX,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,2BAA2B;EAC3B,yBAAyB;EACzB,sBAAsB;EACtB,wBAAwB;AAC1B;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,YAAY;EACZ,kBAAkB;EAClB,oBAAoB;EACpB,mCAAmC;AACrC;;AAEA;EACE,gBAAgB;EAChB,SAAS;EACT,+BAA+B;EAC/B,gBAAgB;EAChB,4BAA4B;EAC5B,iBAAiB;EACjB,eAAe;EACf,yDAAyD;AAC3D;;AAEA;EACE,6BAA6B;EAC7B,0BAA0B;AAC5B;;AAEA,sDAAsD;AACtD;EACE,eAAe;EACf,QAAQ;EACR,aAAa;EACb,sCAAsC;EACtC,0BAA0B;AAC5B;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,+BAA+B;EAC/B,+BAA+B;EAC/B,6BAA6B;EAC7B,4BAA4B;AAC9B;;AAEA,wEAAwE;AACxE;;EAEE,cAAc;EACd,oBAAoB;AACtB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;;;;0EAI0E;AAC1E;EACE,UAAU;EACV,oBAAoB;EACpB,8BAA8B;AAChC;;AAEA;;gFAEgF;;AAEhF;;EAEE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,oBAAoB;EACpB,gCAAgC;EAChC,+BAA+B;EAC/B,6BAA6B;EAC7B,wBAAwB;EACxB,kBAAkB;EAClB,kBAAkB;AACpB;;AAEA;;EAEE,kBAAkB;EAClB,YAAY;AACd;;AAEA;EACE,SAAS;EACT,+BAA+B;EAC/B,0BAA0B;AAC5B;;AAEA;EACE,SAAS;EACT,kBAAkB;EAClB,4BAA4B;AAC9B;;AAEA;;gFAEgF;;AAEhF;;;EAGE,+CAA+C;EAC/C,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,OAAO,UAAU,EAAE;EACnB,KAAK,UAAU,EAAE;AACnB;;AAEA;EACE,OAAO,UAAU,EAAE,2BAA2B,EAAE;EAChD,KAAK,UAAU,EAAE,wBAAwB,EAAE;AAC7C;;AAEA;;gFAEgF;;AAEhF;EACE;IACE,+BAA+B;EACjC;;EAEA;IACE,oBAAoB;IACpB,sBAAsB;EACxB;;EAEA;IACE,cAAc;EAChB;;EAEA;IACE,WAAW;EACb;AACF;;AAEA;EACE;IACE,oBAAoB;IACpB,sBAAsB;EACxB;;EAEA;IACE,aAAa;EACf;;EAEA;;;;IAIE,cAAc;IACd,WAAW;EACb;;EAEA;IACE,sBAAsB;IACtB,+BAA+B;IAC/B,+BAA+B;IAC/B,0BAA0B;IAC1B,gBAAgB;EAClB;;EAEA;;2EAEyE;EACzE;IACE,aAAa;IACb,qBAAqB;IACrB,8BAA8B;IAC9B,YAAY;IACZ,wBAAwB;IACxB,sCAAsC;IACtC,wCAAwC;IACxC,iBAAiB;IACjB,YAAY;EACd;;EAEA;IACE,yBAAyB;IACzB,cAAc;IACd,SAAS;IACT,kBAAkB;IAClB,2BAA2B;IAC3B,yBAAyB;IACzB,sBAAsB;IACtB,wBAAwB;IACxB,gBAAgB;EAClB;;EAEA,+EAA+E;EAC/E;IACE,cAAc;IACd,+BAA+B;IAC/B,gBAAgB;IAChB,+BAA+B;IAC/B,0BAA0B;IAC1B,kBAAkB;EACpB;;EAEA;IACE,aAAa;EACf;;EAEA;sEACoE;EACpE;IACE,cAAc;IACd,gCAAgC;IAChC,gBAAgB;IAChB,+BAA+B;IAC/B,0BAA0B;IAC1B,kBAAkB;EACpB;;EAEA;IACE,aAAa;EACf;;EAEA,uEAAuE;EACvE;IACE,aAAa;EACf;;EAEA;IACE,wCAAwC;EAC1C;;EAEA;IACE,mBAAmB;EACrB;;EAEA,mEAAmE;EACnE;IACE,aAAa;IACb,2BAA2B;IAC3B,WAAW;IACX,8BAA8B;EAChC;;EAEA;IACE,2BAA2B;IAC3B,WAAW;EACb;;EAEA;;IAEE,WAAW;IACX,YAAY;EACd;;EAEA,qEAAqE;EACrE;IACE,aAAa;IACb,2BAA2B;IAC3B,yBAAyB;IACzB,gBAAgB;EAClB;;EAEA;IACE,WAAW;IACX,YAAY;EACd;;EAEA;IACE,gBAAgB;EAClB;AACF;;AAEA;;;;;;+EAM+E;AAC/E;EACE,WAAW;EACX,iBAAiB;EACjB,8BAA8B;EAC9B,eAAe;AACjB;;AAEA;EACE,gBAAgB;EAChB,SAAS;EACT,YAAY;EACZ,SAAS;EACT,cAAc;EACd,wBAAwB;EACxB,eAAe;EACf,kBAAkB;AACpB;;AAEA;EACE,0BAA0B;EAC1B,6BAA6B;AAC/B;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;;AAGA;;;;;;;;gFAQgF;;AAEhF;EACE,aAAa;EACb,eAAe;EACf,QAAQ;EACR,oBAAoB;EACpB,qBAAqB;EACrB,mCAAmC;AACrC;;AAEA,kFAAkF;AAClF;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,mBAAmB;AACrB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,mBAAmB;AACrB;;AAEA,8DAA8D;AAC9D;EACE,aAAa;EACb,eAAe;EACf,qBAAqB;EACrB,QAAQ;EACR,eAAe;EACf,kBAAkB;EAClB,wBAAwB;EACxB,iBAAiB;AACnB;;AAEA;EACE,+BAA+B;EAC/B,4BAA4B;AAC9B;AACA,2EAA2E;AAC3E;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,6BAA6B;AAC/B;;AAEA;EACE,SAAS;EACT,kBAAkB;EAClB,wBAAwB;EACxB,iBAAiB;AACnB;;AAEA;EACE,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,UAAU;EACV,SAAS;EACT,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,eAAe;EACf,0BAA0B;AAC5B;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;EACX,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,WAAW;EACX,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,6BAA6B;AAC/B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,YAAY;AACd;;AAEA;EACE,kBAAkB;EAClB,2BAA2B;EAC3B,0BAA0B;AAC5B;AACA;sDACsD;AACtD;EACE,gBAAgB;AAClB;;AAEA;;gFAEgF;;AAEhF,+DAA+D;AAC/D;EACE,aAAa;EACb,eAAe;EACf,qBAAqB;EACrB,SAAS;AACX;;AAEA;EACE,SAAS;EACT,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,WAAW;AACb;;AAEA;+EAC+E;AAC/E;EACE,gBAAgB;EAChB,gBAAgB;EAChB,cAAc;EACd,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;EACE,WAAW;EACX,yBAAyB;EACzB,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,MAAM;EACN,UAAU;EACV,uBAAuB;EACvB,6BAA6B;EAC7B,sCAAsC;EACtC,gBAAgB;EAChB,mBAAmB;EACnB,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA,+BAA+B,eAAe,EAAE,yBAAiB,EAAjB,iBAAiB,EAAE;AACnE,qCAAqC,0BAA0B,EAAE;;AAEjE;EACE,sBAAsB;EACtB,sCAAsC;EACtC,mBAAmB;AACrB;;AAEA,oDAAoD;AACpD,4BAA4B,mCAAmC,EAAE;;AAEjE;uEACuE;AACvE;EACE,WAAW;EACX,YAAY;EACZ,wBAAwB;EACxB,kBAAkB;AACpB;;AAEA,kCAAkC,eAAe,EAAE,sBAAsB,EAAE;;AAE3E;EACE,eAAe;EACf,kBAAkB;EAClB,qBAAqB;AACvB;;AAEA;;2EAE2E;AAC3E;EACE,aAAa;EACb,sBAAsB;EACtB,gBAAgB;EAChB,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,kBAAkB;EAClB,sBAAsB;AACxB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,SAAS;AACX;;AAEA;EACE,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,eAAe;AACjB;;AAEA;;EAEE,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;AACpB;;AAEA;EACE,cAAc;EACd,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE,cAAc;EACd,gBAAgB;AAClB;;AAEA,sEAAsE;AACtE;EACE,aAAa;EACb,eAAe;EACf,mBAAmB;EACnB,QAAQ;AACV;;AAEA;EACE,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,yBAAyB;EACzB,+CAA+C;EAC/C,oBAAoB;EACpB,yCAAyC;EACzC,0BAA0B;EAC1B,kBAAkB;AACpB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,YAAY;EACZ,SAAS;EACT,kBAAkB;EAClB,uBAAuB;EACvB,4BAA4B;EAC5B,eAAe;AACjB;;AAEA;EACE,qBAAqB;EACrB,mCAAmC;AACrC;;AAEA;;;;;gFAKgF;AAChF;EACE;IACE,uBAAuB;EACzB;;EAEA;IACE,WAAW;IACX,qBAAqB;EACvB;;EAEA,+EAA+E;EAC/E;IACE,qBAAqB;IACrB,sBAAsB;EACxB;;EAEA;IACE,kBAAkB;IAClB,iBAAiB;EACnB;;EAEA;IACE,qBAAqB;IACrB,wBAAwB;EAC1B;;EAEA;;iFAE+E;EAC/E;IACE,gCAAgC;EAClC;AACF;;AAEA;;;;gFAIgF;;AAEhF;;;;;;;gFAOgF;AAChF;EACE,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,eAAe;EACf,gBAAgB;EAChB,YAAY;AACd;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,sBAAsB;EACtB,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,qBAAqB;AACvB;;AAEA;EACE,aAAa;AACf;;AAEA,kFAAkF;AAClF;EACE,aAAa;EACb,qBAAqB;EACrB,eAAe;EACf,cAAc;EACd,YAAY;AACd;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,SAAS;EACT,eAAe;EACf,kBAAkB;EAClB,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;;EAEE,YAAY;AACd;;AAEA,iEAAiE;AACjE;EACE,sBAAsB;EACtB,kBAAkB;AACpB;;AAEA,8EAA8E;AAC9E;EACE,wBAAwB;AAC1B;;AAEA;EACE,aAAa;AACf;;AAEA;;;;;gFAKgF;AAChF;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,WAAW;EACX,qBAAqB;AACvB;;AAEA;EACE,SAAO;AACT;;AAEA,2EAA2E;AAC3E;EACE,aAAa;EACb,mBAAmB;EACnB,iBAAiB;EACjB,WAAW;EACX,iBAAiB;AACnB;;AAEA;EACE,iBAAiB;EACjB,+BAA+B;EAC/B,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;4DAC4D;AAC5D;;EAEE,WAAW;EACX,eAAe;EACf,uBAAuB;EACvB,+BAA+B;EAC/B,+BAA+B;EAC/B,2BAA2B;EAC3B,0BAA0B;EAC1B,oBAAoB;EACpB,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,eAAe;AACjB;;AAEA;;EAEE,eAAe;AACjB;;AAEA;;EAEE,aAAa;EACb,2CAA2C;EAC3C,mDAAmD;AACrD;;AAEA;;EAEE,aAAa;EACb,mBAAmB;AACrB;;AAEA,kEAAkE;AAClE;EACE,oDAAoD;AACtD;;AAEA;EACE,uCAAuC;AACzC;;AAEA,kDAAkD;AAClD;EACE,8CAA8C;AAChD;;AAEA;EACE,cAAc;EACd,eAAe;EACf,iBAAiB;EACjB,gBAAgB;EAChB,qBAAqB;AACvB;;AAEA,+EAA+E;AAC/E;EACE,eAAe;AACjB;;AAEA,qFAAqF;AACrF;EACE,oDAAoD;AACtD;;AAEA;EACE,uCAAuC;AACzC;;AAEA;;;kEAGkE;AAClE;EACE,aAAa;EACb,sBAAsB;EACtB,aAAa;AACf;;AAEA;EACE,eAAe;EACf,QAAQ;EACR,aAAa;EACb,0BAA0B;EAC1B,6BAA6B;AAC/B;;AAEA;EACE,SAAO;EACP,aAAa;EACb,gBAAgB;EAChB,cAAc;AAChB;;AAEA;EACE,gBAAgB;EAChB,MAAM;EACN,UAAU;EACV,6BAA6B;AAC/B;;AAEA;EACE,qBAAqB;AACvB;AACA;;;2BAG2B;AAC3B;EACE,YAAY;EACZ,yBAAiB;UAAjB,iBAAiB;AACnB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,uCAAuC;EACvC,uDAAuD;AACzD;;AAEA;;;6DAG6D;AAC7D;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,gBAAgB;EAChB,gBAAgB;EAChB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,+CAA+C;EAC/C,mBAAmB;EACnB,SAAS;EACT,gBAAgB;EAChB,6BAA6B;EAC7B,+BAA+B;AACjC;;AAEA;EACE,2BAA2B;EAC3B,6BAA6B;AAC/B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,4BAA4B;AAC9B;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,qBAAqB;AACvB","sourcesContent":["/**\r\n * lists.css — Sistema canónico de listados del CMDB.\r\n *\r\n * POR QUÉ EXISTE ESTE FICHERO\r\n * ---------------------------------------------------------------------------\r\n * Los listados (servidores, ubicaciones, racks, PDU/switch/parches, tomas de\r\n * pared, impresoras, estaciones...) habían crecido por copia: cada página se\r\n * llevó el CSS de la anterior y le cambió el prefijo. El resultado medido:\r\n *\r\n *   · `ServersList.css` y `LocationsList.css` eran idénticos en 337 de sus 357\r\n *     líneas (solo cambiaba `servers-` por `locations-`), con derivas pequeñas\r\n *     que son justo la falta de uniformidad: 150px contra 160px, `--accent`\r\n *     contra `#3b82f6`, etc.\r\n *   · `WallOutletsList.css` (779 líneas) era de facto el CSS compartido de\r\n *     todos los listados, pero definía las mismas clases DOS veces: primero\r\n *     con variables y luego con colores claros en duro (`#1e293b`, `#64748b`,\r\n *     `#cbd5e1`) que eran los que acababan mandando. Además tenía un bloque\r\n *     roto: las reglas del kanban de servicios estaban pegadas DENTRO de\r\n *     `.wol-input { ... }`, así que el navegador descartaba reglas al parsear.\r\n *\r\n * Este fichero sustituye todo eso por **una sola definición por cosa**, hecha\r\n * con las variables del sistema de diseño, de modo que los skins de\r\n * `styles/skins.css` la siguen sin tocar nada.\r\n *\r\n * Los tres CSS viejos (`ServersList.css`, `LocationsList.css`, `RacksList.css`)\r\n * y `WallOutletsList.css` YA NO EXISTEN: al migrar los 26 componentes que aún\r\n * lo importaban se les quitó el import, así que no queda CSS de listado fuera\r\n * de este fichero.\r\n *\r\n * CÓMO SE APLICA SIN REESCRIBIR LAS PÁGINAS\r\n * ---------------------------------------------------------------------------\r\n * Se importa el ÚLTIMO de las hojas \"normales\" (después de `overrides.css` y\r\n * antes de `skins.css`), así que gana a los CSS de cada página. Y cada regla\r\n * incluye como alias las clases históricas (`wol-*`, `pel-*` y las\r\n * `*-table` de cada listado) para que las páginas actuales adopten el diseño\r\n * sin cambiar su JSX.\r\n *\r\n * Al añadir un listado nuevo: usa las clases `list-*` y no añadas CSS propio.\r\n * El orden canónico de la página es\r\n *   `.list-page > .list-head + .list-kpis + .list-toolbar + .list-table-wrap`\r\n * y, si hay diálogos, `.list-overlay > .list-dialog`.\r\n */\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   1. Página y cabecera\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-page, .wol-container, .pel-container) {\r\n  max-width: 1400px;\r\n  /* El padding lo pone `.main-content`; aquí solo el aire inferior. */\r\n  padding: 0 0 2rem;\r\n  animation: list-fade-in 0.3s ease;\r\n}\r\n\r\n:is(.list-head, .wol-header, .pel-header) {\r\n  display: flex;\r\n  align-items: center;\r\n  justify-content: space-between;\r\n  flex-wrap: wrap;\r\n  gap: 1rem;\r\n  margin-bottom: 1.5rem;\r\n}\r\n\r\n:is(.list-head-left, .wol-header-left, .pel-header-left) {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 1rem;\r\n  min-width: 0;\r\n}\r\n\r\n/* Grupo de la derecha de la cabecera: conmutador de vistas + botón de alta.\r\n   Antes cada página resolvía esto con estilos en línea. */\r\n:is(.list-head-actions, .wol-header-actions) {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: wrap;\r\n  gap: 0.5rem;\r\n}\r\n\r\n:is(.list-head-icon, .wol-icon, .pel-header-icon) {\r\n  width: 2.25rem;\r\n  height: 2.25rem;\r\n  flex-shrink: 0;\r\n  color: var(--accent);\r\n  opacity: 0.95;\r\n}\r\n\r\n:is(.list-title, .wol-title, .pel-title) {\r\n  margin: 0;\r\n  font-size: 1.5rem;\r\n  font-weight: var(--fw-bold);\r\n  letter-spacing: -0.015em;\r\n  line-height: 1.2;\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.list-subtitle, .wol-subtitle, .pel-subtitle) {\r\n  margin: 0.2rem 0 0;\r\n  font-size: 0.85rem;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n/* ── Listado EMBEBIDO en una sección con pestañas (Agente CMDB) ─────────────\r\n   La página ya tiene su `<h1>` y su barra de pestañas; el listado de dentro es\r\n   una sección, no una pantalla. Para que no compitan dos títulos del mismo\r\n   tamaño, el embebido baja a `<h2>` (`.list-section-title`), el icono se\r\n   reduce y la cabecera se separa con una línea en vez de con aire. */\r\n.list-page--embedded > .list-head {\r\n  align-items: flex-end;\r\n  margin-bottom: 1rem;\r\n  padding-bottom: 0.55rem;\r\n  border-bottom: 1px solid var(--border);\r\n}\r\n\r\n:is(.list-section-title) {\r\n  margin: 0;\r\n  font-size: 1.05rem;\r\n  font-weight: var(--fw-semibold);\r\n  letter-spacing: -0.01em;\r\n  line-height: 1.25;\r\n  color: var(--text-primary);\r\n}\r\n\r\n.list-section-subtitle {\r\n  margin: 0.15rem 0 0;\r\n  font-size: 0.78rem;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.list-page--embedded .list-head-icon {\r\n  width: 1.5rem;\r\n  height: 1.5rem;\r\n  opacity: 0.85;\r\n}\r\n\r\n/* Botón de volver (vistas de detalle dentro de un listado) */\r\n:is(.list-back-btn, .wol-back-btn) {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  padding: 0.35rem 0.5rem;\r\n  margin-right: 0.25rem;\r\n  border: 0;\r\n  border-radius: var(--radius-md);\r\n  background: none;\r\n  color: var(--text-secondary);\r\n  font-size: 1.2rem;\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease, color 0.15s ease;\r\n}\r\n\r\n:is(.list-back-btn, .wol-back-btn):hover {\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-primary);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   2. Barra de filtros y controles\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-toolbar, .wol-filters, .pel-filters) {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: wrap;\r\n  gap: 0.75rem;\r\n  padding: 0.6rem 0.7rem;\r\n  margin-bottom: 1rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n:is(.list-toolbar-left, .wol-list-controls, .pel-toolbar-left) {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: wrap;\r\n  gap: 0.5rem;\r\n}\r\n\r\n/* Los filtros van juntos para poder plegarlos de golpe en pantallas bajas\r\n   (`ListPage` los esconde detrás del botón «Filtros»). */\r\n.list-toolbar-filters {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: wrap;\r\n  gap: 0.5rem;\r\n  min-width: 0;\r\n}\r\n\r\n.list-toolbar-filters.is-collapsed {\r\n  display: none;\r\n}\r\n\r\n/* Botón que abre/cierra la barra de filtros (solo en ventanas bajas/estrechas) */\r\n.list-filters-btn {\r\n  white-space: nowrap;\r\n}\r\n\r\n.list-filters-btn.is-active {\r\n  border-color: rgba(var(--accent-rgb), 0.5);\r\n  color: var(--accent-l);\r\n}\r\n\r\n.list-filters-count {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  min-width: 18px;\r\n  height: 18px;\r\n  padding: 0 5px;\r\n  border-radius: 9px;\r\n  background: var(--accent);\r\n  color: #fff;\r\n  font-size: 0.7rem;\r\n  font-weight: var(--fw-bold);\r\n  line-height: 1;\r\n}\r\n\r\n:is(.list-toolbar-right, .wol-inline-list-controls) {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: wrap;\r\n  gap: 0.5rem;\r\n  margin-left: auto;\r\n}\r\n\r\n:is(.list-control-label, .wol-list-control-label) {\r\n  font-size: 0.68rem;\r\n  font-weight: var(--fw-bold);\r\n  text-transform: uppercase;\r\n  letter-spacing: 0.08em;\r\n  color: var(--text-muted);\r\n}\r\n\r\n/* ── Conmutador de vistas (lista / fichas / árbol) ──────────────────────────\r\n   Solo pinta las vistas que el listado implementa (ver `common/ViewSwitcher`).\r\n   Lleva el nombre de cada vista a propósito: el conmutador anterior era solo\r\n   iconos y no se sabía qué hacía cada botón. */\r\n.list-views {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 2px;\r\n  padding: 2px;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--skin-control-radius, var(--radius-md));\r\n  background: var(--bg-input);\r\n}\r\n\r\n.list-view-btn {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  padding: 0.3rem 0.6rem;\r\n  border: 0;\r\n  border-radius: calc(var(--skin-control-radius, var(--radius-md)) - 2px);\r\n  background: transparent;\r\n  color: var(--text-secondary);\r\n  font-family: inherit;\r\n  font-size: 0.8rem;\r\n  font-weight: var(--fw-semibold);\r\n  line-height: 1;\r\n  white-space: nowrap;\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease, color 0.15s ease;\r\n}\r\n\r\n.list-view-btn svg {\r\n  width: 15px;\r\n  height: 15px;\r\n  flex-shrink: 0;\r\n  stroke-width: 1.8;\r\n}\r\n\r\n.list-view-btn:hover:not(.is-active) {\r\n  background: rgba(var(--accent-rgb), 0.12);\r\n  color: var(--text-primary);\r\n}\r\n\r\n.list-view-btn.is-active {\r\n  background: var(--accent);\r\n  color: #fff;\r\n}\r\n\r\n.list-view-btn--reset {\r\n  padding: 0.3rem 0.45rem;\r\n  margin-left: 2px;\r\n  border-left: 1px solid var(--border);\r\n  border-radius: 0 calc(var(--skin-control-radius, var(--radius-md)) - 2px)\r\n    calc(var(--skin-control-radius, var(--radius-md)) - 2px) 0;\r\n  color: var(--text-muted);\r\n}\r\n\r\n/* En pantallas estrechas queda el icono: el nombre ya lo dice el `title`. */\r\n@media (max-width: 900px) {\r\n  .list-view-btn-label {\r\n    display: none;\r\n  }\r\n}\r\n\r\n/* Contenedor de una vista propia del listado (fichas, árbol…) */\r\n.list-view-body {\r\n  animation: list-fade-in 0.2s ease;\r\n  /* Permite que la vista encoja dentro de un contenedor flex/grid: si no, un\r\n     lienzo ancho (vista Frontal de racks) estira la página en vez de scrollear. */\r\n  min-width: 0;\r\n}\r\n\r\n/* ── Plegado del árbol (vista «árbol» de ubicaciones) ─────────────────────── */\r\n.list-tree-toggle {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  width: 18px;\r\n  height: 18px;\r\n  padding: 0;\r\n  border: 0;\r\n  border-radius: var(--radius-sm);\r\n  background: transparent;\r\n  color: var(--text-muted);\r\n  cursor: pointer;\r\n  flex-shrink: 0;\r\n  transition: background-color 0.15s ease, color 0.15s ease;\r\n}\r\n\r\n.list-tree-toggle svg {\r\n  width: 13px;\r\n  height: 13px;\r\n  stroke-width: 2.2;\r\n}\r\n\r\n.list-tree-toggle:hover {\r\n  background: rgba(var(--accent-rgb), 0.14);\r\n  color: var(--accent-l);\r\n}\r\n\r\n/* Hueco de las hojas del árbol: alinea el icono con el de los nodos plegables */\r\n.list-tree-spacer {\r\n  display: inline-block;\r\n  width: 18px;\r\n  flex-shrink: 0;\r\n}\r\n\r\n/* Rejilla de fichas: la usan las vistas \"card\" de los listados */\r\n.list-cards {\r\n  display: grid;\r\n  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));\r\n  gap: 0.9rem;\r\n}\r\n\r\n.list-card {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 0.5rem;\r\n  padding: 0.9rem 1rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-card);\r\n  box-shadow: var(--shadow-sm);\r\n  cursor: pointer;\r\n  transition: border-color 0.15s ease, box-shadow 0.18s ease, transform 0.12s ease;\r\n}\r\n\r\n.list-card:hover {\r\n  border-color: rgba(var(--accent-rgb), 0.45);\r\n  box-shadow: var(--shadow-lg);\r\n  transform: translateY(-1px);\r\n}\r\n\r\n.list-card-head {\r\n  display: flex;\r\n  align-items: flex-start;\r\n  justify-content: space-between;\r\n  gap: 0.5rem;\r\n}\r\n\r\n.list-card-title {\r\n  font-size: 0.95rem;\r\n  font-weight: var(--fw-bold);\r\n  color: var(--text-primary);\r\n  line-height: 1.25;\r\n}\r\n\r\n.list-card-meta {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  gap: 0.35rem 0.75rem;\r\n  font-size: 0.8rem;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.list-card-actions {\r\n  display: flex;\r\n  justify-content: flex-end;\r\n  gap: 4px;\r\n  margin-top: auto;\r\n}\r\n\r\n/* ── Barra de ocupación de nodos (chasis multinodo) ──────────────────────── */\r\n.chassis-node-bar {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  min-width: 96px;\r\n}\r\n\r\n.chassis-node-bar-track {\r\n  flex: 1;\r\n  height: 6px;\r\n  border-radius: 3px;\r\n  background: var(--slate-200);\r\n  overflow: hidden;\r\n}\r\n\r\n.chassis-node-bar-fill {\r\n  display: block;\r\n  height: 100%;\r\n  transition: width 0.3s ease;\r\n}\r\n\r\n/* ── Sub-filas dentro de una vista (nodos de un chasis) ──────────────────── */\r\n.list-subrows {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 2px;\r\n  margin-top: 0.35rem;\r\n}\r\n\r\n.list-subrow {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 0.5rem;\r\n  width: 100%;\r\n  padding: 0.35rem 0.5rem;\r\n  border: 0;\r\n  border-radius: var(--radius-sm);\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-primary);\r\n  font-family: inherit;\r\n  font-size: 0.82rem;\r\n  text-align: left;\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease;\r\n}\r\n\r\n.list-subrow:hover {\r\n  background: rgba(var(--accent-rgb), 0.12);\r\n}\r\n\r\n.list-subrow-name {\r\n  font-weight: var(--fw-semibold);\r\n  overflow: hidden;\r\n  text-overflow: ellipsis;\r\n  white-space: nowrap;\r\n}\r\n\r\n/* ── Kanban de listados (servidores por entorno) ─────────────────────────── */\r\n.list-kanban {\r\n  display: flex;\r\n  gap: 0.9rem;\r\n  align-items: flex-start;\r\n  overflow-x: auto;\r\n  padding-bottom: 0.5rem;\r\n}\r\n\r\n.list-kanban-col {\r\n  flex: 1 0 260px;\r\n  max-width: 340px;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n.list-kanban-head {\r\n  display: flex;\r\n  align-items: center;\r\n  justify-content: space-between;\r\n  gap: 0.5rem;\r\n  padding: 0.6rem 0.75rem;\r\n  border-bottom: 1px solid var(--border);\r\n  font-size: 0.82rem;\r\n  font-weight: var(--fw-bold);\r\n  color: var(--text-primary);\r\n}\r\n\r\n.list-kanban-body {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 0.6rem;\r\n  padding: 0.65rem;\r\n  max-height: calc(100vh - 320px);\r\n  overflow-y: auto;\r\n}\r\n\r\n/* Fila resaltada por `?destacar=<id>` (convención de todos los módulos) */\r\n.list-row-highlight td {\r\n  background-color: rgba(245, 158, 11, 0.14) !important;\r\n}\r\n\r\n.list-row-highlight:focus-visible td {\r\n  outline: 2px solid var(--amber-600);\r\n  outline-offset: -2px;\r\n}\r\n\r\n/* Caducidad: naranja si queda poco, rojo si ya venció (`rowClassName`).\r\n   Se pinta la fila entera y una barra en la primera celda, sin tocar el texto:\r\n   así se lee igual en tema claro y oscuro. */\r\n.list-table tr.list-row-warn > td {\r\n  background-color: rgba(245, 158, 11, 0.10);\r\n}\r\n\r\n.list-table tr.list-row-warn > td:first-child {\r\n  box-shadow: inset 3px 0 0 var(--amber-600, #d97706);\r\n}\r\n\r\n.list-table tr.list-row-danger > td {\r\n  background-color: rgba(239, 68, 68, 0.10);\r\n}\r\n\r\n.list-table tr.list-row-danger > td:first-child {\r\n  box-shadow: inset 3px 0 0 var(--red-600, #dc2626);\r\n}\r\n\r\n/* Cabecera de grupo de la tabla (ListPage `groupBy`): fila a lo ancho, con el\r\n   triángulo de plegar y el recuento. Cada grupo se pliega por separado. */\r\n.list-group-row td {\r\n  background: var(--bg-toolbar);\r\n  border-top: 1px solid var(--border);\r\n  padding: 0.35rem 0.75rem;\r\n}\r\n\r\n.list-group-toggle {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 0.35rem;\r\n  background: none;\r\n  border: none;\r\n  padding: 0;\r\n  color: var(--text-primary);\r\n  font-size: 0.82rem;\r\n  font-weight: 600;\r\n  cursor: pointer;\r\n}\r\n\r\n.list-group-toggle strong {\r\n  color: var(--text-muted);\r\n  font-weight: 600;\r\n}\r\n\r\n/* ── Selección múltiple y edición masiva ─────────────────────────────────\r\n   La lista pasa `selectable` y `bulkActions`: aparece una casilla por fila\r\n   (y otra en la cabecera para «todo lo visible») y, mientras haya algo\r\n   seleccionado, una barra con las acciones. */\r\n.list-th-select,\r\n.list-select-cell {\r\n  width: 38px;\r\n  text-align: center;\r\n  padding-left: 10px !important;\r\n  padding-right: 0 !important;\r\n}\r\n\r\n.list-th-select input,\r\n.list-select-cell input {\r\n  width: 15px;\r\n  height: 15px;\r\n  margin: 0;\r\n  accent-color: var(--accent);\r\n  cursor: pointer;\r\n  vertical-align: middle;\r\n}\r\n\r\n.list-select-cell {\r\n  cursor: default;\r\n}\r\n\r\n.list-bulk-bar {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 12px;\r\n  flex-wrap: wrap;\r\n  margin-bottom: 10px;\r\n  padding: 8px 12px;\r\n  border: 1px solid rgba(var(--accent-rgb), 0.45);\r\n  border-radius: var(--skin-control-radius, var(--radius-md));\r\n  background: rgba(var(--accent-rgb), 0.10);\r\n}\r\n\r\n.list-bulk-count {\r\n  font-size: 0.85rem;\r\n  color: var(--text-primary);\r\n}\r\n\r\n.list-bulk-count strong {\r\n  font-size: 0.95rem;\r\n}\r\n\r\n.list-bulk-actions {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 8px;\r\n  flex-wrap: wrap;\r\n}\r\n\r\n.list-bulk-bar > .list-btn-secondary {\r\n  margin-left: auto;\r\n}\r\n\r\n/* Campos del diálogo de colocación masiva */\r\n.bulk-field {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 4px;\r\n  margin: 12px 0;\r\n}\r\n\r\n.bulk-field > span {\r\n  font-size: 0.82rem;\r\n  font-weight: 600;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.bulk-field .list-select {\r\n  width: 100%;\r\n}\r\n\r\n:is(.list-control-select, .wol-list-control-select) {\r\n  min-width: 220px;\r\n}\r\n\r\n/* Campos: búsqueda, selectores y entradas de los listados */\r\n:is(.list-search, .list-select, .list-input,\r\n    .wol-search, .wol-select, .wol-input,\r\n    .pel-search, .pel-select, .pel-filter-select, .pel-input) {\r\n  min-width: 200px;\r\n  padding: 0.5rem 0.85rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--skin-control-radius, var(--radius-md));\r\n  background: var(--bg-input);\r\n  color: var(--text-primary);\r\n  font-family: inherit;\r\n  font-size: 0.875rem;\r\n  transition: border-color 0.15s ease, box-shadow 0.15s ease;\r\n}\r\n\r\n:is(.list-search, .wol-search, .pel-search) {\r\n  flex: 1;\r\n}\r\n\r\n:is(.list-select, .wol-select, .pel-select, .pel-filter-select) {\r\n  cursor: pointer;\r\n  padding-right: 2rem;\r\n  appearance: none;\r\n  -webkit-appearance: none;\r\n  /* Flecha dibujada con el color del texto secundario, así vale en claro y oscuro */\r\n  background-image: linear-gradient(45deg, transparent 50%, currentColor 50%),\r\n    linear-gradient(135deg, currentColor 50%, transparent 50%);\r\n  background-position: right 1.1rem center, right 0.75rem center;\r\n  background-size: 5px 5px, 5px 5px;\r\n  background-repeat: no-repeat;\r\n}\r\n\r\n:is(.list-search, .list-select, .list-input, .wol-search, .wol-select, .wol-input,\r\n    .pel-search, .pel-select, .pel-filter-select, .pel-input)::placeholder {\r\n  color: var(--text-muted);\r\n}\r\n\r\n:is(.list-search, .list-select, .list-input, .wol-search, .wol-select, .wol-input,\r\n    .pel-search, .pel-select, .pel-filter-select, .pel-input):focus {\r\n  outline: none;\r\n  border-color: rgba(var(--accent-rgb), 0.55);\r\n  box-shadow: var(--skin-focus-ring, 0 0 0 3px rgba(var(--accent-rgb), 0.18));\r\n}\r\n\r\n:is(.list-select, .wol-select, .pel-select, .pel-filter-select) option {\r\n  background: var(--bg-surface);\r\n  color: var(--text-primary);\r\n}\r\n\r\n/* KPIs de cabecera de un listado (los usan tomas de pared y otros) */\r\n:is(.list-kpis, .wol-kpi-grid) {\r\n  display: grid;\r\n  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));\r\n  gap: 0.65rem;\r\n  margin: 0.75rem 0 1rem;\r\n}\r\n\r\n:is(.list-kpi, .wol-kpi-card) {\r\n  padding: 0.7rem 0.85rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-card);\r\n}\r\n\r\n:is(.list-kpi-value, .wol-kpi-value) {\r\n  font-size: 1.2rem;\r\n  font-weight: var(--fw-bold);\r\n  line-height: 1.1;\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.list-kpi-label, .wol-kpi-label) {\r\n  margin-top: 0.2rem;\r\n  font-size: 0.68rem;\r\n  font-weight: var(--fw-bold);\r\n  text-transform: uppercase;\r\n  letter-spacing: 0.08em;\r\n  color: var(--text-muted);\r\n}\r\n\r\n/* KPIs CLICABLES (Estaciones de Trabajo): cada chip aplica su filtro y\r\n   «Total» los quita todos. El contenedor deja de ser rejilla. */\r\n.list-kpis--chips {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  align-items: stretch;\r\n  gap: 0.6rem;\r\n}\r\n\r\n.list-kpi-chip {\r\n  display: inline-flex;\r\n  flex-direction: column;\r\n  align-items: flex-start;\r\n  gap: 0.1rem;\r\n  padding: 0.55rem 0.8rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-card);\r\n  cursor: pointer;\r\n  text-align: left;\r\n}\r\n\r\n.list-kpi-chip:hover {\r\n  border-color: var(--accent);\r\n}\r\n\r\n.list-kpi-chip--on {\r\n  border-color: var(--accent);\r\n  background: var(--bg-toolbar);\r\n  box-shadow: inset 0 0 0 1px var(--accent);\r\n}\r\n\r\n.list-kpi-group {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 0.4rem;\r\n  margin-left: auto;\r\n  font-size: 0.7rem;\r\n  font-weight: var(--fw-bold);\r\n  text-transform: uppercase;\r\n  letter-spacing: 0.06em;\r\n  color: var(--text-muted);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   3. Tabla\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap,\r\n    .servers-table-wrapper, .locations-table-wrapper, .racks-table-wrapper) {\r\n  overflow: auto;\r\n  -webkit-overflow-scrolling: touch;\r\n  max-height: calc(100vh - 300px);\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-card);\r\n  box-shadow: var(--shadow-sm);\r\n}\r\n\r\n/* `table-layout` no se fija a propósito: las columnas deben repartirse solas. */\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) {\r\n  width: 100%;\r\n  border-collapse: collapse;\r\n  font-size: 13.5px;\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead th {\r\n  position: sticky;\r\n  top: 0;\r\n  z-index: 8;\r\n  padding: 12px 12px;\r\n  border-bottom: 1px solid var(--border);\r\n  /* OPACO a propósito: la cabecera es sticky y con un fondo translúcido se ven\r\n     las filas por debajo al hacer scroll. `--bg-surface` es la variable opaca\r\n     del tema; los skins la sobreescriben con `--skin-table-head-bg`. */\r\n  background: var(--bg-surface, var(--bg-card));\r\n  color: var(--text-muted);\r\n  font-size: 10.5px;\r\n  font-weight: var(--fw-bold);\r\n  text-transform: uppercase;\r\n  letter-spacing: 0.07em;\r\n  text-align: left;\r\n  /* Los encabezados SÍ pueden partir en dos líneas. Con `nowrap` la suma de\r\n     anchos de los 11 encabezados de servidores superaba el contenedor y la\r\n     última columna quedaba cortada tras un scroll horizontal. */\r\n  white-space: normal;\r\n  line-height: 1.3;\r\n  vertical-align: bottom;\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td {\r\n  padding: 13px 12px;\r\n  border-bottom: 1px solid var(--border);\r\n  border-top: 0;\r\n  vertical-align: middle;\r\n  color: var(--text-primary);\r\n  line-height: 1.4;\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:last-child td {\r\n  border-bottom: 0;\r\n}\r\n\r\n/* Cebra muy suave, y en los skins la marca el propio skin */\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:nth-child(even) td {\r\n  background-color: transparent;\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:nth-child(odd) td {\r\n  background-color: var(--skin-row-stripe, var(--bg-toolbar));\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:hover td {\r\n  background-color: var(--skin-row-hover, rgba(var(--accent-rgb), 0.08));\r\n}\r\n\r\n/* Compensación de los estilos en línea que algunas páginas usan para las filas */\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:is(.servers-row-even, .locations-row-even, .racks-row-even, .wol-row-even) td {\r\n  background-color: transparent !important;\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody tr:is(.servers-row-odd, .locations-row-odd, .racks-row-odd, .wol-row-odd) td {\r\n  background-color: var(--skin-row-stripe, var(--bg-toolbar)) !important;\r\n}\r\n\r\n:is(.list-th-sortable, .wol-th-sortable, .list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead th:is(.list-th-sortable, .wol-th-sortable, .sortable, .servers-th, .locations-th, .racks-th) {\r\n  cursor: pointer;\r\n  user-select: none;\r\n}\r\n\r\n:is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead th:is(.list-th-sortable, .wol-th-sortable, .sortable, .servers-th):hover {\r\n  background: rgba(var(--accent-rgb), 0.1);\r\n  color: var(--text-secondary);\r\n}\r\n\r\n:is(.list-sort-indicator, .wol-sort-indicator, .sort-indicator) {\r\n  margin-left: 0.35rem;\r\n  font-size: 0.8rem;\r\n  font-weight: var(--fw-bold);\r\n  color: var(--accent);\r\n}\r\n\r\n:is(.list-sort-indicator, .wol-sort-indicator, .sort-indicator) sup {\r\n  margin-left: 1px;\r\n  font-size: 0.62rem;\r\n}\r\n\r\n:is(.list-row-clickable, .wol-row-clickable) {\r\n  cursor: pointer;\r\n}\r\n\r\n:is(.list-row-clickable, .wol-row-clickable):focus-visible td {\r\n  outline: 2px solid rgba(var(--accent-rgb), 0.8);\r\n  outline-offset: -2px;\r\n}\r\n\r\n/* Fila de grupo plegable */\r\n:is(.list-group-row, .wol-group-row) td {\r\n  padding: 0.45rem 0.9rem;\r\n  background: var(--bg-toolbar) !important;\r\n  border-bottom: 1px solid var(--border);\r\n}\r\n\r\n:is(.list-group-toggle, .wol-group-toggle) {\r\n  padding: 0;\r\n  border: 0;\r\n  background: transparent;\r\n  color: var(--text-primary);\r\n  font-family: inherit;\r\n  font-size: 0.8rem;\r\n  font-weight: var(--fw-semibold);\r\n  cursor: pointer;\r\n}\r\n\r\n:is(.list-group-toggle, .wol-group-toggle) span {\r\n  margin: 0 0.3rem;\r\n}\r\n\r\n:is(.list-group-toggle, .wol-group-toggle) strong {\r\n  color: var(--text-secondary);\r\n  font-weight: var(--fw-semibold);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   4. Contenido en línea de la tabla: códigos, tipos y estados\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-code, .wol-code, .pel-code) {\r\n  padding: 2px 7px;\r\n  border-radius: var(--radius-sm);\r\n  background: rgba(var(--accent-rgb), 0.14);\r\n  color: var(--accent-l);\r\n  font-family: var(--font-mono);\r\n  font-size: 0.8125rem;\r\n}\r\n\r\n:is(.list-type, .wol-type, .pel-model, .pel-name) {\r\n  font-family: var(--font-mono);\r\n  font-size: 0.8rem;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n/* El color funcional sale del token del tema: vale en claro y en oscuro */\r\n:is(.list-state-on, .wol-connected) { color: var(--green-600); font-size: 0.8125rem; }\r\n:is(.list-state-off, .wol-free, .pel-unplaced) { color: var(--text-muted); font-size: 0.8125rem; }\r\n:is(.list-state-cable, .wol-cable) { color: var(--text-muted); font-size: 0.75rem; margin-left: 4px; }\r\n\r\n:is(.list-badge, .wol-badge, .pel-badge) {\r\n  display: inline-block;\r\n  padding: 3px 9px;\r\n  border-radius: var(--skin-chip-radius, 12px);\r\n  font-size: 0.72rem;\r\n  font-weight: var(--fw-semibold);\r\n  letter-spacing: 0.02em;\r\n}\r\n\r\n:is(.list-badge--on, .wol-badge--active, .pel-badge--active) {\r\n  background: rgba(22, 163, 74, 0.14);\r\n  color: var(--green-600);\r\n}\r\n\r\n:is(.list-badge--off, .wol-badge--inactive, .pel-badge--offline) {\r\n  background: rgba(220, 38, 38, 0.14);\r\n  color: var(--red-600);\r\n}\r\n\r\n:is(.list-badge--warn, .pel-badge--maintenance) {\r\n  background: rgba(217, 119, 6, 0.16);\r\n  color: var(--amber-600);\r\n}\r\n\r\n/* Dos tonos más, para no tener que inventar colores en línea en cada pantalla:\r\n   «accent» es un estado en curso (no es bueno ni malo todavía) y «muted» es un\r\n   estado apagado (cancelado) que no debe leerse como error. */\r\n:is(.list-badge--accent) {\r\n  background: rgba(var(--accent-rgb), 0.16);\r\n  color: var(--accent-l);\r\n}\r\n\r\n:is(.list-badge--muted) {\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-secondary);\r\n  border: 1px solid var(--border);\r\n}\r\n\r\n:is(.list-hint, .pel-hint) {\r\n  font-size: 0.8rem;\r\n  color: var(--text-muted);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   5. Columna de acciones\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-actions, .wol-actions, .pel-actions, .servers-actions, .locations-actions, .racks-actions) {\r\n  display: flex;\r\n  align-items: center;\r\n  justify-content: flex-end;\r\n  gap: 4px;\r\n}\r\n\r\n:is(.list-actions-header, .servers-actions-header, .locations-actions-header, .racks-actions-header) {\r\n  text-align: right;\r\n  width: 1%;\r\n  white-space: nowrap;\r\n}\r\n\r\n:is(.list-action-btn, .list-icon-btn,\r\n    .wol-action-btn, .wol-btn-sm, .wol-btn-sm-edit, .wol-btn-sm-delete,\r\n    .pel-action-btn) {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  width: 30px;\r\n  height: 30px;\r\n  padding: 0;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--skin-control-radius, 6px);\r\n  background: var(--bg-input);\r\n  color: var(--text-secondary);\r\n  font-size: 0;\r\n  line-height: 1;\r\n  cursor: pointer;\r\n  flex-shrink: 0;\r\n  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;\r\n}\r\n\r\n:is(.list-action-btn, .list-icon-btn, .wol-action-btn, .wol-btn-sm,\r\n    .wol-btn-sm-edit, .wol-btn-sm-delete, .pel-action-btn) svg {\r\n  width: 15px;\r\n  height: 15px;\r\n  stroke-width: 1.8;\r\n}\r\n\r\n:is(.list-action-btn, .list-icon-btn, .wol-action-btn, .wol-btn-sm,\r\n    .wol-btn-sm-edit, .pel-action-btn):hover {\r\n  background: rgba(var(--accent-rgb), 0.14);\r\n  border-color: rgba(var(--accent-rgb), 0.5);\r\n  color: var(--accent-l);\r\n}\r\n\r\n:is(.list-action-btn--danger, .wol-action-btn--delete, .wol-btn-sm-delete,\r\n    .pel-action-btn--delete):hover {\r\n  background: rgba(220, 38, 38, 0.14);\r\n  border-color: rgba(220, 38, 38, 0.45);\r\n  color: var(--red-600);\r\n}\r\n\r\n.action-icon {\r\n  font-size: 0.95rem;\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   6. Botones\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-btn-primary, .wol-btn-primary, .pel-btn-primary) {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  padding: 0.5rem 1rem;\r\n  border: 0;\r\n  border-radius: var(--skin-control-radius, var(--radius-md));\r\n  background: var(--skin-btn-primary-bg, var(--accent));\r\n  color: #fff;\r\n  font-family: inherit;\r\n  font-size: 0.875rem;\r\n  font-weight: var(--fw-semibold);\r\n  white-space: nowrap;\r\n  cursor: pointer;\r\n  transition: filter 0.15s ease, box-shadow 0.18s ease, transform 0.12s ease;\r\n}\r\n\r\n:is(.list-btn-primary, .wol-btn-primary, .pel-btn-primary) svg {\r\n  width: 16px;\r\n  height: 16px;\r\n  flex-shrink: 0;\r\n}\r\n\r\n:is(.list-btn-primary, .wol-btn-primary, .pel-btn-primary):hover:not(:disabled) {\r\n  filter: brightness(1.08);\r\n  box-shadow: 0 8px 20px -10px rgba(var(--accent-rgb), 0.8);\r\n  transform: translateY(-1px);\r\n}\r\n\r\n:is(.list-btn-secondary, .wol-btn-secondary, .pel-btn-secondary) {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  padding: 0.5rem 1rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--skin-control-radius, var(--radius-md));\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-secondary);\r\n  font-family: inherit;\r\n  font-size: 0.875rem;\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;\r\n}\r\n\r\n:is(.list-btn-secondary, .wol-btn-secondary, .pel-btn-secondary) svg {\r\n  width: 16px;\r\n  height: 16px;\r\n  flex-shrink: 0;\r\n}\r\n\r\n:is(.list-btn-secondary, .wol-btn-secondary, .pel-btn-secondary):hover:not(:disabled) {\r\n  border-color: rgba(var(--accent-rgb), 0.45);\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.list-btn-danger, .wol-btn-danger, .pel-btn-danger) {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  padding: 0.5rem 1rem;\r\n  border: 1px solid rgba(220, 38, 38, 0.35);\r\n  border-radius: var(--skin-control-radius, var(--radius-md));\r\n  background: rgba(220, 38, 38, 0.12);\r\n  color: var(--red-600);\r\n  font-family: inherit;\r\n  font-size: 0.875rem;\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease, border-color 0.15s ease;\r\n}\r\n\r\n:is(.list-btn-danger, .wol-btn-danger, .pel-btn-danger):hover:not(:disabled) {\r\n  background: rgba(220, 38, 38, 0.2);\r\n  border-color: rgba(220, 38, 38, 0.55);\r\n}\r\n\r\n:is(.list-btn-primary, .list-btn-secondary, .list-btn-danger,\r\n    .wol-btn-primary, .wol-btn-secondary, .wol-btn-danger,\r\n    .pel-btn-primary, .pel-btn-secondary, .pel-btn-danger):disabled {\r\n  opacity: 0.5;\r\n  cursor: not-allowed;\r\n  transform: none;\r\n  box-shadow: none;\r\n}\r\n\r\n/* Botón pequeño de acción con texto (presets de puertos, etc.) */\r\n:is(.list-chip-btn, .pel-preset-btn) {\r\n  padding: 0.3rem 0.65rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--skin-chip-radius, var(--radius-md));\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-secondary);\r\n  font-family: inherit;\r\n  font-size: 0.78rem;\r\n  font-weight: var(--fw-semibold);\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;\r\n}\r\n\r\n:is(.list-chip-btn, .pel-preset-btn):hover,\r\n:is(.list-chip-btn, .pel-preset-btn).active {\r\n  background: rgba(var(--accent-rgb), 0.14);\r\n  border-color: rgba(var(--accent-rgb), 0.5);\r\n  color: var(--accent-l);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   7. Menú de columnas\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-column-toggle, .wol-column-toggle, .column-toggle-menu) {\r\n  position: relative;\r\n}\r\n\r\n:is(.list-column-dropdown, .wol-column-dropdown, .column-menu-dropdown) {\r\n  position: absolute;\r\n  top: 100%;\r\n  right: 0;\r\n  z-index: 50;\r\n  margin-top: 0.5rem;\r\n  padding: 0.5rem;\r\n  min-width: 240px;\r\n  max-height: 400px;\r\n  overflow-y: auto;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-lg);\r\n  /* Superficie OPACA: en los skins Lumina `--bg-card` es translúcido y un\r\n     desplegable flotando sobre la tabla se volvería ilegible. */\r\n  background: var(--bg-surface, var(--bg-card));\r\n  box-shadow: var(--shadow-lg);\r\n  animation: list-slide-down 0.18s ease;\r\n}\r\n\r\n:is(.list-column-item, .wol-column-item, .column-menu-item) {\r\n  display: flex;\r\n  align-items: center;\r\n  padding: 0.5rem 0.75rem;\r\n  border-radius: var(--radius-md);\r\n  color: var(--text-secondary);\r\n  font-size: 0.86rem;\r\n  cursor: pointer;\r\n  user-select: none;\r\n  transition: background-color 0.15s ease, color 0.15s ease;\r\n}\r\n\r\n:is(.list-column-item, .wol-column-item, .column-menu-item):hover {\r\n  background-color: rgba(var(--accent-rgb), 0.1);\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.list-column-item, .wol-column-item, .column-menu-item) input[type=\"checkbox\"] {\r\n  width: 16px;\r\n  height: 16px;\r\n  margin-right: 0.75rem;\r\n  accent-color: var(--accent);\r\n  cursor: pointer;\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   8. Diálogos: confirmación y formulario\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-overlay, .wol-overlay, .pel-modal-overlay) {\r\n  position: fixed;\r\n  inset: 0;\r\n  /* Por encima del menú lateral (1100) y de los modales de Bootstrap (1055):\r\n     con 1050 el diálogo salía DETRÁS del menú. El `Dialog` se pinta además en\r\n     `document.body` (portal), porque dentro de un ancestro con `transform` el\r\n     `position: fixed` se queda pegado a ese ancestro. */\r\n  z-index: 1200;\r\n  display: flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  padding: 1rem;\r\n  background: var(--modal-overlay);\r\n  backdrop-filter: blur(4px);\r\n}\r\n\r\n:is(.list-dialog, .wol-confirm, .pel-confirm-modal) {\r\n  width: 100%;\r\n  max-width: 420px;\r\n  padding: 1.5rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-xl);\r\n  /* Igual que el menú de columnas: superficie opaca para que el modal no se\r\n     transparente en los skins Lumina. */\r\n  background: var(--bg-surface, var(--bg-card));\r\n  box-shadow: var(--shadow-lg);\r\n}\r\n\r\n:is(.list-dialog, .wol-confirm, .pel-confirm-modal) h4 {\r\n  margin: 0 0 0.5rem;\r\n  font-size: 1rem;\r\n  font-weight: var(--fw-bold);\r\n  color: var(--text-primary);\r\n}\r\n\r\n/* ── Diálogo común: cabecera fija, cuerpo con scroll y acciones a la vista ──\r\n   Sin esto, un diálogo con contenido largo (plantillas, listas de equipos)\r\n   empujaba los botones fuera de la pantalla. El icono de la cabecera se\r\n   acota: un SVG sin tamaño se pinta a todo el ancho disponible. */\r\n.list-dialog {\r\n  display: flex;\r\n  flex-direction: column;\r\n  max-height: 88vh;\r\n}\r\n\r\n.list-dialog > h4 {\r\n  flex: none;\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 8px;\r\n}\r\n\r\n.list-dialog-icon {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  flex: none;\r\n  color: var(--accent);\r\n}\r\n\r\n.list-dialog-icon svg {\r\n  width: 20px;\r\n  height: 20px;\r\n}\r\n\r\n.list-dialog-body {\r\n  flex: 1 1 auto;\r\n  min-height: 0;\r\n  overflow-y: auto;\r\n  overflow-x: hidden;\r\n  /* Un pelo de aire para que el foco de los campos no se recorte. */\r\n  padding-right: 2px;\r\n}\r\n\r\n.list-dialog > .list-dialog-actions {\r\n  flex: none;\r\n  margin-top: 1rem;\r\n  padding-top: 0.75rem;\r\n  border-top: 1px solid var(--border);\r\n}\r\n\r\n:is(.list-dialog, .wol-confirm, .pel-confirm-modal) p {\r\n  margin: 0 0 1.25rem;\r\n  font-size: 0.875rem;\r\n  line-height: 1.5;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n:is(.list-dialog-actions, .wol-confirm-actions, .pel-confirm-actions) {\r\n  display: flex;\r\n  justify-content: flex-end;\r\n  gap: 0.75rem;\r\n}\r\n\r\n:is(.list-form-modal, .wol-form-modal, .pel-form-modal) {\r\n  width: 100%;\r\n  max-width: 580px;\r\n  overflow: hidden;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-xl);\r\n  background: var(--bg-surface);\r\n  box-shadow: var(--shadow-lg);\r\n}\r\n\r\n:is(.list-form-header, .wol-form-header, .pel-form-header) {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 0.75rem;\r\n  padding: 1rem 1.25rem;\r\n  border-bottom: 1px solid var(--border);\r\n  background: transparent;\r\n}\r\n\r\n:is(.list-form-header, .wol-form-header, .pel-form-header) :is(h3, h4) {\r\n  flex: 1;\r\n  margin: 0;\r\n  font-size: 1rem;\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.list-form-body, .wol-form-body, .pel-form-body) {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 0.875rem;\r\n  padding: 1.25rem;\r\n}\r\n\r\n:is(.list-form-row, .wol-form-row, .pel-form-row) {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  gap: 0.75rem;\r\n}\r\n\r\n:is(.list-form-field, .wol-form-field, .pel-form-field) {\r\n  display: flex;\r\n  flex: 1;\r\n  flex-direction: column;\r\n  gap: 0.3rem;\r\n  min-width: 120px;\r\n}\r\n\r\n:is(.list-form-field, .wol-form-field, .pel-form-field) :is(label, span:first-child) {\r\n  font-size: 0.68rem;\r\n  font-weight: var(--fw-bold);\r\n  text-transform: uppercase;\r\n  letter-spacing: 0.07em;\r\n  color: var(--text-muted);\r\n}\r\n\r\n:is(.list-form-actions, .wol-form-actions, .pel-form-actions) {\r\n  display: flex;\r\n  justify-content: flex-end;\r\n  gap: 0.75rem;\r\n  margin-top: 0.5rem;\r\n  padding-top: 0.75rem;\r\n  border-top: 1px solid var(--border);\r\n}\r\n\r\n:is(.list-close, .wol-close, .pel-form-close) {\r\n  padding: 4px 8px;\r\n  border: 0;\r\n  border-radius: var(--radius-sm);\r\n  background: none;\r\n  color: var(--text-secondary);\r\n  font-size: 1.1rem;\r\n  cursor: pointer;\r\n  transition: background-color 0.15s ease, color 0.15s ease;\r\n}\r\n\r\n:is(.list-close, .wol-close, .pel-form-close):hover {\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-primary);\r\n}\r\n\r\n/* Bootstrap: los modales clásicos de estos listados */\r\n.modal-backdrop.fade.show {\r\n  position: fixed;\r\n  inset: 0;\r\n  z-index: 1050;\r\n  background-color: var(--modal-overlay);\r\n  backdrop-filter: blur(4px);\r\n}\r\n\r\n.modal.show .modal-dialog {\r\n  z-index: 1060;\r\n}\r\n\r\n.modal-content {\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-xl);\r\n  background: var(--bg-surface);\r\n  box-shadow: var(--shadow-lg);\r\n}\r\n\r\n/* Los modales abiertos a mano (sin el JS de Bootstrap) necesitan esto */\r\n.modal.modal-open-fix,\r\n.modal.fade.show {\r\n  display: block;\r\n  pointer-events: auto;\r\n}\r\n\r\nbody.modal-open {\r\n  overflow: hidden;\r\n}\r\n\r\n/* Con un diálogo abierto, el menú lateral y la barra superior se APAGAN del\r\n   todo en vez de quedarse medio visibles bajo el oscurecido: a media luz se ve\r\n   sucio y compite con la ventana. No se sacan del flujo (nada de `display:none`)\r\n   porque la ficha está maquetada para ese ancho: solo se apaga su opacidad, así\r\n   que el alto y el ancho no cambian y los formularios no se descuadran. */\r\nbody.modal-open :is(.sidebar, .navbar-custom) {\r\n  opacity: 0;\r\n  pointer-events: none;\r\n  transition: opacity 120ms ease;\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   9. Estados vacíos y de carga\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n:is(.list-loading, .list-empty, .wol-loading, .wol-empty, .pel-loading, .pel-empty,\r\n    .servers-empty-state, .locations-empty-state, .racks-empty-state) {\r\n  display: flex;\r\n  flex-direction: column;\r\n  align-items: center;\r\n  justify-content: center;\r\n  gap: 0.6rem;\r\n  padding: 3.5rem 2rem;\r\n  border: 1px dashed var(--border);\r\n  border-radius: var(--radius-lg);\r\n  background: var(--bg-toolbar);\r\n  color: var(--text-muted);\r\n  font-size: 0.95rem;\r\n  text-align: center;\r\n}\r\n\r\n:is(.list-empty, .wol-empty, .pel-empty, .servers-empty-state,\r\n    .locations-empty-state, .racks-empty-state) :is(span, .empty-icon, .pel-empty-icon) {\r\n  font-size: 2.25rem;\r\n  opacity: 0.8;\r\n}\r\n\r\n:is(.empty-title, .list-empty-title) {\r\n  margin: 0;\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-primary);\r\n}\r\n\r\n:is(.empty-subtitle, .list-empty-subtitle) {\r\n  margin: 0;\r\n  font-size: 0.85rem;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   10. Detalles globales\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\nbutton:focus-visible,\r\na:focus-visible,\r\n:is(.list-column-item, .wol-column-item, .column-menu-item):focus-visible {\r\n  outline: 2px solid rgba(var(--accent-rgb), 0.7);\r\n  outline-offset: 2px;\r\n  box-shadow: none;\r\n}\r\n\r\n.btn-primary .btn-icon-left {\r\n  margin-right: 0.35rem;\r\n}\r\n\r\n@keyframes list-fade-in {\r\n  from { opacity: 0; }\r\n  to { opacity: 1; }\r\n}\r\n\r\n@keyframes list-slide-down {\r\n  from { opacity: 0; transform: translateY(-8px); }\r\n  to { opacity: 1; transform: translateY(0); }\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   11. Pantallas pequeñas: la tabla pasa a fichas apiladas\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n@media (max-width: 992px) {\r\n  :is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap) {\r\n    max-height: calc(100vh - 260px);\r\n  }\r\n\r\n  :is(.list-toolbar, .wol-filters, .pel-filters) {\r\n    align-items: stretch;\r\n    flex-direction: column;\r\n  }\r\n\r\n  :is(.list-toolbar-right, .wol-inline-list-controls) {\r\n    margin-left: 0;\r\n  }\r\n\r\n  :is(.list-toolbar-right, .wol-inline-list-controls) button {\r\n    width: 100%;\r\n  }\r\n}\r\n\r\n@media (max-width: 768px) {\r\n  :is(.list-head, .wol-header, .pel-header) {\r\n    align-items: stretch;\r\n    flex-direction: column;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) thead {\r\n    display: none;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table),\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tbody,\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr,\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td {\r\n    display: block;\r\n    width: 100%;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr {\r\n    margin-bottom: 0.75rem;\r\n    border: 1px solid var(--border);\r\n    border-radius: var(--radius-lg);\r\n    background: var(--bg-card);\r\n    overflow: hidden;\r\n  }\r\n\r\n  /* Cada fila se lee como una ficha: etiqueta a la izquierda y valor a la\r\n     derecha. Antes cada celda ocupaba una línea entera con su etiqueta encima,\r\n     así que un listado de 12 columnas medía media pantalla por registro. */\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td {\r\n    display: flex;\r\n    align-items: baseline;\r\n    justify-content: space-between;\r\n    gap: 0.75rem;\r\n    padding: 0.38rem 0.85rem;\r\n    border-bottom: 1px solid var(--border);\r\n    background-color: transparent !important;\r\n    text-align: right;\r\n    min-width: 0;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td[data-label]::before {\r\n    content: attr(data-label);\r\n    flex: 0 1 auto;\r\n    margin: 0;\r\n    font-size: 0.68rem;\r\n    font-weight: var(--fw-bold);\r\n    text-transform: uppercase;\r\n    letter-spacing: 0.06em;\r\n    color: var(--text-muted);\r\n    text-align: left;\r\n  }\r\n\r\n  /* La primera celda es el nombre del registro: sin etiqueta y a la izquierda. */\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td:first-child:not(.list-select-cell) {\r\n    display: block;\r\n    padding: 0.7rem 0.85rem 0.45rem;\r\n    text-align: left;\r\n    font-weight: var(--fw-semibold);\r\n    color: var(--text-primary);\r\n    font-size: 0.92rem;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td:first-child:not(.list-select-cell)::before {\r\n    display: none;\r\n  }\r\n\r\n  /* Con selección múltiple la primera celda es la casilla: el nombre es la\r\n     siguiente y tiene que seguir leyéndose como título de la ficha. */\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell + td {\r\n    display: block;\r\n    padding: 0.35rem 0.85rem 0.45rem;\r\n    text-align: left;\r\n    font-weight: var(--fw-semibold);\r\n    color: var(--text-primary);\r\n    font-size: 0.92rem;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell + td::before {\r\n    display: none;\r\n  }\r\n\r\n  /* Sin datos no se pinta la línea: sería una fila de «—» por columna. */\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-cell--empty {\r\n    display: none;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr:hover td {\r\n    background-color: transparent !important;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td:last-child {\r\n    border-bottom: none;\r\n  }\r\n\r\n  /* Acciones: botones alineados a la izquierda y con área de dedo. */\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) tr > td:last-child:not([data-label]) {\r\n    display: flex;\r\n    justify-content: flex-start;\r\n    gap: 0.5rem;\r\n    padding: 0.5rem 0.85rem 0.6rem;\r\n  }\r\n\r\n  :is(.list-actions, .wol-actions, .pel-actions, .servers-actions, .locations-actions, .racks-actions) {\r\n    justify-content: flex-start;\r\n    gap: 0.5rem;\r\n  }\r\n\r\n  :is(.list-action-btn, .list-icon-btn, .wol-action-btn, .wol-btn-sm,\r\n      .wol-btn-sm-edit, .wol-btn-sm-delete, .pel-action-btn) {\r\n    width: 36px;\r\n    height: 36px;\r\n  }\r\n\r\n  /* La celda de selección múltiple se queda arriba, junto al nombre. */\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell {\r\n    display: flex;\r\n    justify-content: flex-start;\r\n    padding: 0.6rem 0.85rem 0;\r\n    border-bottom: 0;\r\n  }\r\n\r\n  :is(.list-table, .wol-table, .pel-table, .servers-table, .locations-table, .racks-table) td.list-select-cell input {\r\n    width: 20px;\r\n    height: 20px;\r\n  }\r\n\r\n  :is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap) {\r\n    max-height: none;\r\n  }\r\n}\r\n\r\n/* ══════════════════════════════════════════════════════════════════════════\r\n   Rueda dentada de columnas\r\n   Vive al final de la CABECERA de la tabla (no en la barra de herramientas: el\r\n   botón con texto «Columnas» ocupaba sitio útil ahí). El desplegable se pinta\r\n   fuera de la tabla con `position: fixed` (ver `ListPage`), porque la tabla\r\n   hace scroll y un menú absoluto dentro del `thead` quedaría recortado.\r\n   ══════════════════════════════════════════════════════════════════════════ */\r\n.list-th-cog {\r\n  width: 42px;\r\n  text-align: right;\r\n  padding-right: 12px !important;\r\n  cursor: default;\r\n}\r\n\r\n.list-cog-btn {\r\n  background: none;\r\n  border: 0;\r\n  padding: 2px;\r\n  margin: 0;\r\n  line-height: 1;\r\n  color: var(--text-muted);\r\n  cursor: pointer;\r\n  border-radius: 6px;\r\n}\r\n\r\n.list-cog-btn:hover {\r\n  color: var(--text-primary);\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n.list-cog-btn svg {\r\n  width: 15px;\r\n  height: 15px;\r\n}\r\n\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   18. Modal de «Documentos vinculados» (DocSmartButton)\r\n\r\n   Las cinco acciones de este modal estaban en el pie como botones grandes\r\n   (`list-btn-secondary`, 0.5rem de relleno): con cuatro de ellos el texto partía\r\n   en dos líneas y la fila entera quedaba pegada a la lista. Ahora las cuatro\r\n   acciones de creación viven en una barra propia dentro del cuerpo, en pequeño,\r\n   y el pie se queda solo con Cerrar.\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n.doc-modal-actions {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  gap: 6px;\r\n  margin-top: 0.875rem;\r\n  padding-top: 0.875rem;\r\n  border-top: 1px solid var(--border);\r\n}\r\n\r\n/* Chip de acción: el `list-chip-btn` de la casa, pero con icono y sin partirse. */\r\n.doc-chip {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  white-space: nowrap;\r\n}\r\n\r\n.doc-chip svg {\r\n  width: 14px;\r\n  height: 14px;\r\n  flex-shrink: 0;\r\n}\r\n\r\n.doc-chip:disabled {\r\n  opacity: 0.55;\r\n  cursor: not-allowed;\r\n}\r\n\r\n/* Renglón de «quién y cuándo» bajo el título del documento. */\r\n.doc-row-meta {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  align-items: baseline;\r\n  gap: 4px;\r\n  margin-top: 3px;\r\n  font-size: 0.72rem;\r\n  color: var(--text-muted);\r\n  line-height: 1.35;\r\n}\r\n\r\n.doc-row-meta strong {\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-secondary);\r\n}\r\n/* Traza del documento en el editor (pestaña Meta) y filas del historial. */\r\n.doc-meta-traza {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 2px;\r\n  padding: 0.55rem 0.7rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-sm);\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n.doc-meta-traza p {\r\n  margin: 0;\r\n  font-size: 0.75rem;\r\n  color: var(--text-muted);\r\n  line-height: 1.45;\r\n}\r\n\r\n.doc-meta-traza strong {\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.doc-link-btn {\r\n  padding: 0;\r\n  border: 0;\r\n  background: none;\r\n  font: inherit;\r\n  color: var(--accent-l);\r\n  cursor: pointer;\r\n  text-decoration: underline;\r\n}\r\n\r\n.doc-hist {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 0.4rem;\r\n  margin-top: 0.5rem;\r\n}\r\n\r\n.doc-hist-row {\r\n  display: flex;\r\n  align-items: center;\r\n  justify-content: space-between;\r\n  gap: 0.5rem;\r\n  padding: 0.45rem 0.6rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-sm);\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n.doc-hist-head {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  flex-wrap: wrap;\r\n  min-width: 0;\r\n}\r\n\r\n.doc-hist-ver {\r\n  font-size: 0.82rem;\r\n  font-weight: var(--fw-bold);\r\n  color: var(--text-primary);\r\n}\r\n/* El pie del modal de documentos respira: los chips de acción ya llevan su\r\n   separador y el botón Cerrar salía pegado a ellos. */\r\n.doc-modal .list-dialog-actions {\r\n  margin-top: 1rem;\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   Alta masiva de tomas de pared — tabla editable dentro del diálogo\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n/* Fila de campos de la «semilla» y del generador de códigos. */\r\n.bulk-field-row {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  align-items: flex-end;\r\n  gap: 10px;\r\n}\r\n\r\n.bulk-field-row .bulk-field {\r\n  margin: 0;\r\n  flex: 1 1 140px;\r\n  min-width: 120px;\r\n}\r\n\r\n.bulk-field-row .list-input {\r\n  width: 100%;\r\n}\r\n\r\n/* La tabla puede ser larga (48 tomas de un edificio): se desplaza ella sola,\r\n   no el diálogo entero, para que la cabecera y los botones sigan a la vista. */\r\n.bulk-grid-wrap {\r\n  margin-top: 10px;\r\n  max-height: 44vh;\r\n  overflow: auto;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-md);\r\n}\r\n\r\n.bulk-grid {\r\n  width: 100%;\r\n  border-collapse: collapse;\r\n  font-size: 0.82rem;\r\n}\r\n\r\n.bulk-grid thead th {\r\n  position: sticky;\r\n  top: 0;\r\n  z-index: 1;\r\n  padding: 0.45rem 0.5rem;\r\n  background: var(--bg-surface);\r\n  border-bottom: 1px solid var(--border);\r\n  text-align: left;\r\n  white-space: nowrap;\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.bulk-grid thead th.sortable { cursor: pointer; user-select: none; }\r\n.bulk-grid thead th.sortable:hover { color: var(--text-primary); }\r\n\r\n.bulk-grid td {\r\n  padding: 0.2rem 0.3rem;\r\n  border-bottom: 1px solid var(--border);\r\n  vertical-align: top;\r\n}\r\n\r\n/* Fila con un código que el backend ha rechazado. */\r\n.bulk-grid tr.is-error td { background: rgba(239, 68, 68, 0.08); }\r\n\r\n/* Los campos de una celda van justos: el `min-width: 200px` de la regla\r\n   general de listados ensancharía la tabla hasta hacerla inservible. */\r\n.bulk-grid :is(input, select) {\r\n  width: 100%;\r\n  min-width: 0;\r\n  padding: 0.32rem 0.45rem;\r\n  font-size: 0.82rem;\r\n}\r\n\r\n.bulk-grid td:first-child input { min-width: 96px; font-family: monospace; }\r\n\r\n.bulk-grid-error {\r\n  margin: 2px 0 0;\r\n  font-size: 0.72rem;\r\n  color: var(--red-600);\r\n}\r\n\r\n/* ── Diálogo de alta masiva: más ancho y con campos más grandes ─────────────\r\n   Cabe la cadena completa (panel + switch) sin que la tabla quede apretada:\r\n   con 11 columnas y letra de 0,82rem había que hacer zoom para trabajar. */\r\n.bulk-dialog {\r\n  display: flex;\r\n  flex-direction: column;\r\n  padding: 1.75rem;\r\n  max-height: 94vh;\r\n  overflow-y: auto;\r\n}\r\n\r\n.bulk-dialog h4 {\r\n  font-size: 1.15rem;\r\n  margin-bottom: 0.85rem;\r\n}\r\n\r\n.bulk-dialog .bulk-field > span {\r\n  font-size: 0.9rem;\r\n}\r\n\r\n.bulk-dialog .bulk-field-row {\r\n  gap: 12px;\r\n}\r\n\r\n.bulk-dialog .bulk-field-row .bulk-field {\r\n  flex: 1 1 170px;\r\n  min-width: 150px;\r\n}\r\n\r\n.bulk-dialog .bulk-field-row .bulk-field--switches {\r\n  flex: 2 1 300px;\r\n}\r\n\r\n.bulk-dialog .list-input,\r\n.bulk-dialog .list-select {\r\n  padding: 0.55rem 0.7rem;\r\n  font-size: 0.95rem;\r\n}\r\n\r\n.bulk-dialog .bulk-grid {\r\n  font-size: 0.95rem;\r\n}\r\n\r\n.bulk-dialog .bulk-grid thead th {\r\n  padding: 0.6rem 0.55rem;\r\n  font-size: 0.88rem;\r\n}\r\n\r\n.bulk-dialog .bulk-grid td {\r\n  padding: 0.3rem 0.35rem;\r\n}\r\n\r\n.bulk-dialog .bulk-grid :is(input, select) {\r\n  padding: 0.46rem 0.55rem;\r\n  font-size: 0.95rem;\r\n}\r\n\r\n.bulk-dialog .bulk-grid-wrap {\r\n  flex: 1 1 auto;\r\n  min-height: 180px;\r\n  max-height: none;\r\n}\r\n\r\n.bulk-dialog .list-dialog-actions {\r\n  flex: 0 0 auto;\r\n  margin-top: 14px;\r\n}\r\n\r\n/* Selector múltiple de switches de la semilla: chips + desplegable. */\r\n.bulk-switch-picker {\r\n  display: flex;\r\n  flex-wrap: wrap;\r\n  align-items: center;\r\n  gap: 6px;\r\n}\r\n\r\n.bulk-switch-picker .list-select {\r\n  flex: 1 1 180px;\r\n  min-width: 160px;\r\n}\r\n\r\n.bulk-chip {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 6px;\r\n  padding: 4px 6px 4px 10px;\r\n  border: 1px solid rgba(var(--accent-rgb), 0.35);\r\n  border-radius: 999px;\r\n  background: rgba(var(--accent-rgb), 0.14);\r\n  color: var(--text-primary);\r\n  font-size: 0.85rem;\r\n}\r\n\r\n.bulk-chip button {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  justify-content: center;\r\n  padding: 2px;\r\n  border: 0;\r\n  border-radius: 50%;\r\n  background: transparent;\r\n  color: var(--text-secondary);\r\n  cursor: pointer;\r\n}\r\n\r\n.bulk-chip button:hover {\r\n  color: var(--red-600);\r\n  background: rgba(239, 68, 68, 0.12);\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   12. Pantallas BAJAS (portátil de 14\": 1366×768 y, con escalado al 125 %,\r\n       1092×614). Aquí el problema no es el ancho sino el alto: cada franja de\r\n       aire se paga en filas que no se ven. Se recorta lo decorativo y la tabla\r\n       se queda con el alto que sobre. En pantallas altas no cambia nada.\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n@media (max-height: 820px) {\r\n  :is(.list-page, .wol-container, .pel-container) {\r\n    padding-bottom: 0.75rem;\r\n  }\r\n\r\n  :is(.list-head, .wol-header, .pel-header) {\r\n    gap: 0.6rem;\r\n    margin-bottom: 0.8rem;\r\n  }\r\n\r\n  /* Fichas embebidas (con pestañas de sección): la línea separadora ya separa. */\r\n  .list-page--embedded > .list-head {\r\n    margin-bottom: 0.6rem;\r\n    padding-bottom: 0.4rem;\r\n  }\r\n\r\n  :is(.list-subtitle, .wol-subtitle, .pel-subtitle) {\r\n    margin-top: 0.1rem;\r\n    font-size: 0.8rem;\r\n  }\r\n\r\n  :is(.list-toolbar, .wol-filters, .pel-filters) {\r\n    margin-bottom: 0.6rem;\r\n    padding: 0.45rem 0.55rem;\r\n  }\r\n\r\n  /* La tabla ocupa lo que queda del viewport en vez de dejar un hueco muerto.\r\n     Con la cabecera de una fila el chrome baja a ~196 px: 250 dejaría ~35 px\r\n     sin usar; 215 los aprovecha (si se abren los filtros, la página scrollea). */\r\n  :is(.list-table-wrap, .wol-table-wrap, .pel-table-wrap) {\r\n    max-height: calc(100dvh - 215px);\r\n  }\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   13. Densidad de las tablas → `styles/density.css`\r\n       (va después de `skins.css` porque tiene que ganar al relleno del skin;\r\n       ver `theme/density.js` para el valor y los umbrales).\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   14. Cabecera de UNA fila en pantalla baja (`.list-page--compact`)\r\n       La pone `ListPage` cuando `useCompactViewport()` dice que la ventana es\r\n       baja o estrecha. El buscador y el botón de filtros suben a la cabecera\r\n       (`.list-head-search`), la barra de herramientas desaparece y la cabecera\r\n       se aprieta: icono fuera, subtítulo en la misma línea que el título y\r\n       botones más pequeños. Ganancia real en un 14\": ~50 px de alto.\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n.list-head-search {\r\n  display: flex;\r\n  align-items: center;\r\n  gap: 0.35rem;\r\n  flex: 1 1 200px;\r\n  max-width: 340px;\r\n  min-width: 0;\r\n}\r\n\r\n.list-search--inline {\r\n  width: 100%;\r\n  min-width: 0;\r\n  padding: 0.4rem 0.7rem;\r\n  font-size: 0.82rem;\r\n}\r\n\r\n.list-page--compact :is(.list-head, .wol-header) {\r\n  gap: 0.5rem;\r\n  margin-bottom: 0.6rem;\r\n}\r\n\r\n.list-page--compact .list-head-icon {\r\n  display: none;\r\n}\r\n\r\n/* Título y contador en la misma línea: la cabecera pasa de dos renglones a uno. */\r\n.list-page--compact .list-head-left > div {\r\n  display: flex;\r\n  align-items: baseline;\r\n  flex-wrap: wrap;\r\n  gap: 0 0.55rem;\r\n  min-width: 0;\r\n}\r\n\r\n.list-page--compact :is(.list-title, .list-section-title) {\r\n  font-size: 1.1rem;\r\n}\r\n\r\n.list-page--compact :is(.list-subtitle, .list-section-subtitle) {\r\n  margin: 0;\r\n  max-width: 46ch;\r\n  font-size: 0.76rem;\r\n  white-space: nowrap;\r\n  overflow: hidden;\r\n  text-overflow: ellipsis;\r\n}\r\n\r\n.list-page--compact .list-head-actions,\r\n.list-page--compact .list-head-search {\r\n  gap: 0.35rem;\r\n}\r\n\r\n/* Botones de la cabecera y de la barra: un punto más pequeños. */\r\n.list-page--compact :is(.list-head-actions, .list-toolbar) :is(.list-btn-primary, .list-btn-secondary) {\r\n  padding: 0.4rem 0.7rem;\r\n  font-size: 0.82rem;\r\n}\r\n\r\n/* El conmutador de vistas se queda en iconos (el nombre ya va en el title). */\r\n.list-page--compact .list-view-btn {\r\n  padding: 0.35rem 0.45rem;\r\n}\r\n\r\n.list-page--compact .list-view-btn-label {\r\n  display: none;\r\n}\r\n\r\n/* ═══════════════════════════════════════════════════════════════════════════\r\n   15. Tabla EDITABLE dentro de un listado (vista «Alta rápida» de tomas)\r\n       Una vista propia pinta una tabla con celdas de formulario. Se reutilizan\r\n       las clases `list-table` / `list-table-wrap` para que mida y se comporte\r\n       igual que la tabla normal; aquí solo van las piezas de edición.\r\n   ═══════════════════════════════════════════════════════════════════════════ */\r\n.list-grid-bar {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: wrap;\r\n  gap: 0.5rem;\r\n  margin-bottom: 0.6rem;\r\n}\r\n\r\n.list-grid-spacer {\r\n  flex: 1;\r\n}\r\n\r\n/* Acciones a la derecha; el grupo no se parte aunque la barra haga wrap. */\r\n.list-grid-actions {\r\n  display: flex;\r\n  align-items: center;\r\n  flex-wrap: nowrap;\r\n  gap: 0.5rem;\r\n  margin-left: auto;\r\n}\r\n\r\n.list-grid-title {\r\n  font-size: 0.9rem;\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-primary);\r\n  white-space: nowrap;\r\n}\r\n\r\n.list-table--editable td {\r\n  vertical-align: top;\r\n}\r\n\r\n/* Celda de formulario: ocupa el ancho de la columna y no impone el min-width\r\n   de 200 px de `.list-input` (la tabla tiene 9 columnas). */\r\n.list-cell-input,\r\n.list-cell-select {\r\n  width: 100%;\r\n  min-width: 88px;\r\n  padding: 0.3rem 0.45rem;\r\n  border: 1px solid var(--border);\r\n  border-radius: var(--radius-sm);\r\n  background: var(--bg-input);\r\n  color: var(--text-primary);\r\n  font-family: inherit;\r\n  font-size: 0.82rem;\r\n}\r\n\r\n.list-cell-select {\r\n  min-width: 118px;\r\n  cursor: pointer;\r\n}\r\n\r\n.list-cell-input--short,\r\n.list-cell-select--short {\r\n  min-width: 72px;\r\n}\r\n\r\n.list-cell-input:focus,\r\n.list-cell-select:focus {\r\n  outline: none;\r\n  border-color: rgba(var(--accent-rgb), 0.55);\r\n  box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.15);\r\n}\r\n\r\n.list-cell-input:disabled,\r\n.list-cell-select:disabled {\r\n  opacity: 0.55;\r\n  cursor: not-allowed;\r\n}\r\n\r\n/* Fila nueva: se distingue de las existentes (que van en gris). */\r\n.list-row--new td {\r\n  background: rgba(var(--accent-rgb), 0.07) !important;\r\n}\r\n\r\n.list-row--new td:first-child {\r\n  box-shadow: inset 3px 0 0 var(--accent);\r\n}\r\n\r\n/* Fila con error del backend o código repetido. */\r\n.list-table--editable tr.is-error td {\r\n  background: rgba(220, 38, 38, 0.12) !important;\r\n}\r\n\r\n.list-cell-error {\r\n  display: block;\r\n  margin-top: 3px;\r\n  font-size: 0.7rem;\r\n  font-weight: 600;\r\n  color: var(--red-600);\r\n}\r\n\r\n/* Tomas ya creadas: editables en línea (código, nombre, sala, planta, tipo). */\r\n.list-row--locked {\r\n  cursor: default;\r\n}\r\n\r\n/* Fila con cambios sin guardar: se marca con el acento y una banda a la izquierda. */\r\n.list-row--locked.is-dirty td {\r\n  background: rgba(var(--accent-rgb), 0.06) !important;\r\n}\r\n\r\n.list-row--locked.is-dirty td:first-child {\r\n  box-shadow: inset 3px 0 0 var(--accent);\r\n}\r\n\r\n/* ── Modo «hoja de cálculo» (pantalla completa) ────────────────────────────\r\n   Cubre la aplicación entera (menú lateral y barra superior incluidos) y el\r\n   alto lo consume la tabla, con la cabecera de columnas pegada arriba. En un\r\n   14\" son ~70 px más de filas y desaparece todo lo que distrae. */\r\n.list-grid {\r\n  display: flex;\r\n  flex-direction: column;\r\n  min-height: 0;\r\n}\r\n\r\n.list-grid--full {\r\n  position: fixed;\r\n  inset: 0;\r\n  z-index: 1200;\r\n  padding: 0.75rem 1rem 1rem;\r\n  background: var(--bg-surface);\r\n}\r\n\r\n.list-grid--full .list-table-wrap {\r\n  flex: 1;\r\n  min-height: 0;\r\n  max-height: none;\r\n  overflow: auto;\r\n}\r\n\r\n.list-grid--full .list-table thead th {\r\n  position: sticky;\r\n  top: 0;\r\n  z-index: 2;\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n.list-grid--full .list-grid-bar {\r\n  margin-bottom: 0.6rem;\r\n}\r\n/* ── Columnas arrastrables desde la cabecera (2026-10-30) ──────────────────\r\n   `ListPage` marca cada `th` con `draggable`. Aquí solo se le dice al usuario\r\n   que se puede mover y dónde va a caer: asa con el cursor y una línea vertical\r\n   en la columna destino. */\r\n.list-table thead th.list-th-draggable {\r\n  cursor: grab;\r\n  user-select: none;\r\n}\r\n\r\n.list-table thead th.list-th-draggable:active {\r\n  cursor: grabbing;\r\n}\r\n\r\n.list-table thead th.list-th-draggable.is-dragging {\r\n  opacity: 0.45;\r\n}\r\n\r\n.list-table thead th.list-th-draggable.is-drop-target {\r\n  box-shadow: inset 3px 0 0 var(--accent);\r\n  background: rgba(var(--accent-rgb, 59, 130, 246), 0.08);\r\n}\r\n\r\n/* ── Edición masiva de campos (BulkEditAction) ───────────────────────────────\r\n   Cada campo es una fila: casilla + nombre a la izquierda y el editor a la\r\n   derecha (solo cuando la casilla está marcada). Las filas marcadas llevan\r\n   fondo para que se vea de un vistazo qué se va a cambiar. */\r\n.bulk-edit-fields {\r\n  display: flex;\r\n  flex-direction: column;\r\n  gap: 4px;\r\n  margin-top: 10px;\r\n  max-height: 46vh;\r\n  overflow-y: auto;\r\n  padding-right: 2px;\r\n}\r\n\r\n.bulk-edit-field {\r\n  display: grid;\r\n  grid-template-columns: minmax(140px, 210px) 1fr;\r\n  align-items: center;\r\n  gap: 10px;\r\n  padding: 5px 8px;\r\n  border: 1px solid transparent;\r\n  border-radius: var(--radius-md);\r\n}\r\n\r\n.bulk-edit-field.is-on {\r\n  border-color: var(--border);\r\n  background: var(--bg-toolbar);\r\n}\r\n\r\n.bulk-edit-check {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 8px;\r\n  cursor: pointer;\r\n  font-weight: var(--fw-semibold);\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.bulk-edit-check input {\r\n  accent-color: var(--accent);\r\n}\r\n\r\n.bulk-edit-field.is-on .bulk-edit-check {\r\n  color: var(--text-primary);\r\n}\r\n\r\n.bulk-edit-field :is(.list-input, .list-select) {\r\n  width: 100%;\r\n  min-width: 0;\r\n}\r\n\r\n.bulk-edit-switch {\r\n  display: inline-flex;\r\n  align-items: center;\r\n  gap: 8px;\r\n  cursor: pointer;\r\n  color: var(--text-secondary);\r\n}\r\n\r\n.bulk-edit-switch input {\r\n  accent-color: var(--accent);\r\n}\r\n\r\n.bulk-edit-dialog h4 {\r\n  margin-bottom: 0.5rem;\r\n}\r\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
